import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { resolveWritingScore, resolveWritingMaxScore, getWritingNclcFromScore } from "@/utils/writingResultUtils";
import { translateErrorCategory } from "@/utils/errorTranslation";
import { translateCompetencyLabel, translateStatusLabel, translateActionLabel } from "@/utils/translationHelpers";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ContextModal } from "@/components/ui/ContextModal";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from "recharts";
import { BookOpen, Headphones, PenTool, Layers, Activity, Flame, Sparkles, AlertCircle, Maximize2, Loader2, Award, Clock, Calendar, ArrowRight, RefreshCw, CheckCircle2, Circle, ChevronDown, ChevronUp, X, ClipboardCheck, Check } from "lucide-react";
import { userGoalService } from "@/services/userGoalService";
import type { UserGoalsRecord } from "@/services/userGoalService";
import { studyTimeService } from "@/services/studyTimeService";

import { calculateProgressToGoal, getSkillStatus, getBestSkillResult } from "@/utils/progressUtils";
import { generateStudyRecommendations, generateStudyPlan } from "@/utils/studyRecommendations";
import { historyService } from "@/services/historyService";
import { isSupabaseConfigured } from "@/lib/supabaseClient";
import type { HistoryItem } from "@/services/historyService";
import { flashcardsData, writingErrorsData } from "@/data/mockData";
import { formatPercentage, formatCompactScore } from "@/lib/utils";
import { streakService } from "@/services/streakService";
import { getCompetencyLabel, getTefLevelByScore, getUserGoals, getScoreGapToGoal, getTargetScoreForNclc } from "@/data/tefScoreScale";
import { writingCorrectionHistoryService } from "@/services/writingCorrectionHistoryService";
import { flashcardService } from "@/services/flashcardService";
import { errorBankService } from "@/services/errorBankService";
import { isWritingV2Result, isLegacyWritingResult } from "@/utils/writingV2Helpers";


// EE v2 extraction and identification helpers
function isWritingV2HistoryRecord(record: any): boolean {
  return isWritingV2Result(record);
}

function getWritingV2Section(record: any): string {
  const sec = record?.metadata?.section || record?.section || "Section A";
  const lower = String(sec).trim().toLowerCase();
  if (lower.includes("tâche 1") || lower.includes("tache 1") || lower.includes("task 1") || lower.includes("section a") || lower === "a") {
    return "Section A";
  }
  if (lower.includes("tâche 2") || lower.includes("tache 2") || lower.includes("task 2") || lower.includes("section b") || lower === "b") {
    return "Section B";
  }
  return "Section A";
}

function getWritingV2WordCount(record: any): number {
  return Number(record?.metadata?.wordCount || record?.words || 0);
}

function getWritingV2ErrorCount(record: any): number {
  return Number(record?.metadata?.errorCount || record?.metadata?.writingCorrection?.errors?.length || 0);
}

function getWritingV2FocusAreas(record: any): string[] {
  return record?.metadata?.mainFocusAreas || record?.metadata?.writingCorrection?.mainFocusAreas || [];
}

function getWritingV2Errors(record: any): any[] {
  return record?.metadata?.writingCorrection?.errors || [];
}

function isWritingV2Flashcard(card: any): boolean {
  return (
    card?.source === "ee_pedagogical_correction" ||
    card?.source === "ee_v2_pedagogical_correction" ||
    card?.metadata?.writingVersion === 2 ||
    card?.metadata?.createdFrom === "writing_v2_error" ||
    card?.metadata?.staticDeck === "writing_v2"
  );
}

// Competence check helpers
const isReadingItem = (item: any) => {
  const comp = (item.competence || "").toLowerCase();
  return comp === "reading" || comp.includes("écrite") || comp.includes("ce");
};

const isListeningItem = (item: any) => {
  const comp = (item.competence || "").toLowerCase();
  return comp === "listening" || comp.includes("orale") || comp.includes("co");
};

const isWritingItem = (item: any) => {
  const comp = (item.competence || "").toLowerCase();
  return comp === "writing" || comp.includes("écrite") || comp.includes("ee");
};

// Utility to parse percentage from scores
function parseScorePercentage(score: any, percentageField?: number, competence?: string): number {
  if (typeof percentageField === "number" && percentageField <= 100) return percentageField;
  
  if (typeof score === "number") {
    if (score <= 100) return score;
    const comp = (competence || "").trim().toLowerCase();
    if (comp.includes("listening") || comp.includes("co") || comp.includes("orale")) {
      return (score / 360) * 100;
    }
    if (comp.includes("reading") || comp.includes("ce") || comp.includes("écrite")) {
      return (score / 300) * 100;
    }
    if (comp.includes("writing") || comp.includes("ee") || comp.includes("écrite")) {
      return (score / 450) * 100;
    }
    return (score / 360) * 100;
  }
  
  if (!score || typeof score !== "string") return 0;
  
  if (score.includes("%")) {
    const match = score.match(/(\d+(?:\.\d+)?)\s*%/);
    if (match) return parseFloat(match[1]);
  }
  
  const parts = score.split("/");
  if (parts.length === 2) {
    const num = parseFloat(parts[0]);
    const den = parseFloat(parts[1]);
    if (!isNaN(num) && !isNaN(den) && den > 0) {
      return Math.round((num / den) * 100);
    }
  }
  
  const parsed = parseFloat(score);
  if (isNaN(parsed)) return 0;
  if (parsed > 100) {
    const comp = (competence || "").trim().toLowerCase();
    if (comp.includes("listening") || comp.includes("co") || comp.includes("orale")) {
      return (parsed / 360) * 100;
    }
    if (comp.includes("reading") || comp.includes("ce") || comp.includes("écrite")) {
      return (parsed / 300) * 100;
    }
    if (comp.includes("writing") || comp.includes("ee") || comp.includes("écrite")) {
      return (parsed / 450) * 100;
    }
    return (parsed / 360) * 100;
  }
  return parsed;
}

// Utility to parse score out of 300 points
function getAverageScore(items: HistoryItem[], competence: "Reading" | "Listening") {
  const filtered = items.filter(item => {
    const isComp = item.test_type === "complete" || item.testType === "complete" || item.mode === "full_exam" || item.competence === "Simulado Completo";
    if (isComp) return true;
    return competence === "Reading" ? isReadingItem(item) : isListeningItem(item);
  });
  
  const maxScore = competence === "Reading" ? 300 : 360;
  let sum = 0;
  let count = 0;
  
  filtered.forEach(item => {
    const isComp = item.test_type === "complete" || item.testType === "complete" || item.mode === "full_exam" || item.competence === "Simulado Completo";
    if (isComp) {
      const ce = item.result?.ce || item.sections?.reading || {};
      const co = item.result?.co || item.sections?.listening || {};
      const targetSec = competence === "Reading" ? ce : co;
      const pct = targetSec.percentage ?? targetSec.percent ?? targetSec.percentScore;
      if (typeof pct === "number") {
        sum += (pct / 100) * maxScore;
        count++;
      }
    } else {
      const pct = parseScorePercentage(item.score, item.percentage, competence);
      if (pct > 0) {
        sum += (pct / 100) * maxScore;
        count++;
      }
    }
  });
  
  return count > 0 ? Math.round(sum / count) : 0;
}

const getSectionDisplay = (item: any, t: any) => {
  if (item.metadata?.type === "writing_linguistic_correction" && item.metadata?.writingVersion === 2) {
    const sec = item.metadata?.section || item.section || "Section A";
    const translatedSec = sec === "Section A" ? t("writingErrors.sectionA", "Section A") : sec === "Section B" ? t("writingErrors.sectionB", "Section B") : sec;
    const mode = item.metadata?.testMode === "complete" 
      ? t("history.sectionDisplay.sectionV2", "{{section}} — Full EE test", { section: translatedSec }).replace(`${translatedSec} — `, "")
      : t("history.sectionDisplay.sectionV2Individual", "{{section}} — Individual", { section: translatedSec }).replace(`${translatedSec} — `, "");
    return `${translatedSec} — ${mode}`;
  }
  if (item.mode === "full_exam") return t("history.sectionDisplay.fullSimulated", "Simulado completo");
  if (item.selectedSection) return item.selectedSection;
  if (item.section) return item.section;
  
  if (item.sectionResults) {
    const keys = Object.keys(item.sectionResults);
    if (keys.length === 1) return keys[0];
    if (keys.length > 1) return t("history.all", "Todas");
  }
  
  return t("history.all", "Todas");
};

const formatSecondsToHoursMinutes = (seconds: number, t: any): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  console.log("[LANGUAGE ACTIVE]", currentLanguage);
  
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [isRefreshingCoach, setIsRefreshingCoach] = useState(false);
  const [coachTextOverride, setCoachTextOverride] = useState<string | null>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<HistoryItem | null>(null);
  const [isChartExpanded, setIsChartExpanded] = useState(false);

  const [isPerformanceExpanded, setIsPerformanceExpanded] = useState(() => {
    const saved = localStorage.getItem("dashboard_performance_expanded");
    return saved !== null ? saved === "true" : true;
  });
  const [isWeaknessMapExpanded, setIsWeaknessMapExpanded] = useState(() => {
    const saved = localStorage.getItem("dashboard_weakness_map_expanded");
    return saved !== null ? saved === "true" : true;
  });

  const togglePerformance = () => {
    setIsPerformanceExpanded(prev => {
      const newVal = !prev;
      localStorage.setItem("dashboard_performance_expanded", String(newVal));
      return newVal;
    });
  };

  const toggleWeaknessMap = () => {
    setIsWeaknessMapExpanded(prev => {
      const newVal = !prev;
      localStorage.setItem("dashboard_weakness_map_expanded", String(newVal));
      return newVal;
    });
  };

  const handleScorePointClick = (historyId: string) => {
    if (historyId) {
      const found = historyItems.find(item => item.id === historyId);
      if (found) {
        setSelectedAttempt(found);
      }
    }
  };

  const CustomClickableDot = (props: any) => {
    const { cx, cy, payload, dataKey } = props;
    if (!payload) return null;

    const historyId = dataKey === "reading" ? payload.readingHistoryId : payload.listeningHistoryId;
    if (!historyId) return null;

    const dotColor = dataKey === "reading" ? "#8b5cf6" : "#3b82f6";

    return (
      <g style={{ cursor: "pointer" }}>
        {/* Invisible larger touch/click target */}
        <circle 
          cx={cx} 
          cy={cy} 
          r={12} 
          fill="transparent" 
          onClick={(e) => {
            e.stopPropagation();
            handleScorePointClick(historyId);
          }} 
        />
        {/* Real visual dot */}
        <circle 
          cx={cx} 
          cy={cy} 
          r={6} 
          fill={dotColor} 
          stroke="white" 
          strokeWidth={2} 
          onClick={(e) => {
            e.stopPropagation();
            handleScorePointClick(historyId);
          }} 
        />
      </g>
    );
  };

  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [flashcards, setFlashcards] = useState<any[]>([]);
  const [writingErrors, setWritingErrors] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [studyTimeToday, setStudyTimeToday] = useState(0);
  const [studyTimeWeekly, setStudyTimeWeekly] = useState(0);
  const [studyTimeByCompetency, setStudyTimeByCompetency] = useState({
    reading: 0,
    listening: 0,
    writing: 0,
    flashcards: 0,
    errorBank: 0,
    general: 0
  });

  const [userGoals, setUserGoalsState] = useState(() => getUserGoals());

  const [performanceTab, setPerformanceTab] = useState<"all" | "reading" | "listening" | "writing">("all");
  const [completedTasks, setCompletedTasks] = useState<Record<string, boolean>>({});
  
  // Custom toast notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  // Personal goals state
  const [personalGoals, setPersonalGoals] = useState<UserGoalsRecord | null>(null);
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [editingGoals, setEditingGoals] = useState({
    targetCeNclc: 7,
    targetCoNclc: 7,
    targetEeNclc: 7,
    targetEoNclc: 7,
    weeklyStudyHours: 7,
    examDate: ""
  });
  const [savingGoals, setSavingGoals] = useState(false);
  const [isChecklistDismissed, setIsChecklistDismissed] = useState(() => localStorage.getItem("tef_getting_started_dismissed") === "true");
  const [isChecklistCollapsed, setIsChecklistCollapsed] = useState(() => localStorage.getItem("tef_getting_started_collapsed") === "true");

  const handleSaveGoals = async () => {
    try {
      setSavingGoals(true);
      const updated = await userGoalService.upsertUserGoals({
        targetCeNclc: Number(editingGoals.targetCeNclc),
        targetCoNclc: Number(editingGoals.targetCoNclc),
        targetEeNclc: Number(editingGoals.targetEeNclc),
        targetEoNclc: Number(editingGoals.targetEoNclc),
        weeklyStudyHours: Number(editingGoals.weeklyStudyHours),
        examDate: editingGoals.examDate || null
      });
      setPersonalGoals(updated);
      setIsGoalModalOpen(false);
      alert(t("dashboard.goalSavedSuccess", "Personal goal successfully updated!"));
    } catch (err) {
      console.error("Error saving goals", err);
      alert(t("settings.statsUnavailable", "Could not save goals."));
    } finally {
      setSavingGoals(false);
    }
  };

  // Compute checklist items
  const checklistSetGoal = Boolean(personalGoals?.metadata?.onboardingCompleted || personalGoals?.metadata?.isLegacyBypass);
  const checklistFirstReading = historyItems.some(item => item.competence === "Reading");
  const checklistFirstListening = historyItems.some(item => item.competence === "Listening");
  const checklistFirstEssay = historyItems.some(item => item.competence === "Writing");
  const checklistFirstErrors = writingErrors.length > 0 || flashcards.length > 0;

  const checklistItems = [
    { id: "setGoal", label: t("gettingStarted.setGoal", "Set your goal"), checked: checklistSetGoal },
    { id: "firstReading", label: t("gettingStarted.firstReading", "Complete your first Reading test"), checked: checklistFirstReading },
    { id: "firstListening", label: t("gettingStarted.firstListening", "Complete your first Listening test"), checked: checklistFirstListening },
    { id: "firstEssay", label: t("gettingStarted.firstEssay", "Write your first essay"), checked: checklistFirstEssay },
    { id: "firstErrors", label: t("gettingStarted.firstErrors", "Review your first errors"), checked: checklistFirstErrors }
  ];

  const checklistCompletedCount = checklistItems.filter(item => item.checked).length;
  const isChecklistAllCompleted = checklistCompletedCount === checklistItems.length;

  const fetchDashboardData = async () => {
    try {
      const [std, fCards, errors, pGoals] = await Promise.all([
        historyService.getTestHistory(),
        flashcardService.getFlashcards(),
        errorBankService.getErrors(),
        userGoalService.getUserGoals(),
      ]);

      console.log("[Dashboard] loaded history", { count: std.length });
      
      let merged = [];
      if (isSupabaseConfigured) {
        merged = std;
      } else {
        const wc = writingCorrectionHistoryService.getWritingCorrections().map(c => ({
          ...c,
          isWritingCorrection: true,
          id: c.id,
          date: c.savedAt || c.createdAt,
          competence: "Writing" as const,
          score: `${c.estimatedScore}/${c.maxScore}`,
          timeSpent: "N/A",
          status: "Corrigido por IA",
          words: c.wordCount,
          selectedSection: c.section
        }));
        merged = [...std, ...wc];
      }
      
      const sorted = merged.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setHistoryItems(sorted);
      setFlashcards(fCards);
      setWritingErrors(errors);
      setUserGoalsState(getUserGoals());
      setPersonalGoals(pGoals);

      const uid = user?.id;
      if (uid) {
        try {
          const [, todayTime, weeklyTime, competencyTime] = await Promise.all([
            streakService.getStreakDataAsync().catch(streakErr => {
              console.error("Error fetching streak data in dashboard:", streakErr);
              return null;
            }),
            studyTimeService.getTodayStudyTime(uid),
            studyTimeService.getWeeklyStudyTime(uid),
            studyTimeService.getStudyTimeByCompetency(uid)
          ]);

          setStudyTimeToday(todayTime);
          setStudyTimeWeekly(weeklyTime);
          setStudyTimeByCompetency(competencyTime);

          console.log("[SYNC_SOURCE_STUDY_TIME]", {
            source: "supabase",
            todaySeconds: todayTime,
            weekSeconds: weeklyTime,
          });
        } catch (stErr) {
          console.error("Error fetching study times in dashboard:", stErr);
        }
      } else {
        // User not yet authenticated — keep study time at 0, will reload after auth
        console.log("[SYNC_SOURCE_STUDY_TIME]", { source: "skipped_no_auth" });
      }

    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchDashboardData();
  }, [user?.id]); // Re-fetch when userId becomes available after auth resolves


  // Fetch updated dashboard data on broadcast event
  useEffect(() => {
    const updateHandler = () => {
      fetchDashboardData();
    };
    
    window.addEventListener("historyUpdated", updateHandler);
    window.addEventListener("flashcardsUpdated", updateHandler);
    window.addEventListener("dashboardUpdated", updateHandler);
    window.addEventListener("storage", updateHandler); // sync on cross tab changes
    
    return () => {
      window.removeEventListener("historyUpdated", updateHandler);
      window.removeEventListener("flashcardsUpdated", updateHandler);
      window.removeEventListener("dashboardUpdated", updateHandler);
      window.removeEventListener("storage", updateHandler);
    };
  }, []);

  // Load tasks and checklist collapse/dismiss state from Supabase goals metadata
  useEffect(() => {
    if (!personalGoals) return;

    try {
      // 1. Checklist Dismissed
      if (personalGoals.metadata?.gettingStartedChecklist?.dismissed !== undefined) {
        setIsChecklistDismissed(Boolean(personalGoals.metadata.gettingStartedChecklist.dismissed));
      } else {
        setIsChecklistDismissed(localStorage.getItem("tef_getting_started_dismissed") === "true");
      }

      // 2. Checklist Collapsed
      if (personalGoals.metadata?.gettingStartedChecklist?.collapsed !== undefined) {
        setIsChecklistCollapsed(Boolean(personalGoals.metadata.gettingStartedChecklist.collapsed));
      } else {
        setIsChecklistCollapsed(localStorage.getItem("tef_getting_started_collapsed") === "true");
      }

      // 3. Completed Tasks
      const todayStr = new Date().toLocaleDateString("sv-SE");
      if (personalGoals.metadata?.studyPlanProgress?.date === todayStr) {
        setCompletedTasks(personalGoals.metadata.studyPlanProgress.tasks || {});
      } else {
        // Different day or no progress, load from localStorage fallback if same day, or start empty
        const storageKey = `tef_today_study_plan_${todayStr}`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          setCompletedTasks(JSON.parse(saved));
        } else {
          setCompletedTasks({});
        }
      }

      // Clean up old localStorage keys
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("tef_today_study_plan_") && key !== `tef_today_study_plan_${todayStr}`) {
          localStorage.removeItem(key);
        }
      });
    } catch (err) {
      console.error("Error synchronizing dashboard states from goals metadata:", err);
    }
  }, [personalGoals]);

  const toggleTaskCompletion = async (taskId: string) => {
    try {
      const todayStr = new Date().toLocaleDateString("sv-SE");
      const storageKey = `tef_today_study_plan_${todayStr}`;
      const updated = {
        ...completedTasks,
        [taskId]: !completedTasks[taskId]
      };
      setCompletedTasks(updated);
      localStorage.setItem(storageKey, JSON.stringify(updated));

      if (personalGoals) {
        const updatedMeta = {
          ...personalGoals.metadata,
          studyPlanProgress: {
            date: todayStr,
            tasks: updated
          }
        };
        const res = await userGoalService.upsertUserGoals({ metadata: updatedMeta });
        setPersonalGoals(res);
      }
    } catch (err) {
      console.error("Error saving completed task:", err);
    }
  };

  const hasData = historyItems.length > 0;

  // --- STATS CALCULATIONS ---
  const readingAverage = getAverageScore(historyItems, "Reading");
  const listeningAverage = getAverageScore(historyItems, "Listening");
  
  const hasReadingData = historyItems.some(item => isReadingItem(item) || item.test_type === "complete" || item.testType === "complete" || item.mode === "full_exam" || item.competence === "Simulado Completo") && readingAverage > 0;
  const hasListeningData = historyItems.some(item => isListeningItem(item) || item.test_type === "complete" || item.testType === "complete" || item.mode === "full_exam" || item.competence === "Simulado Completo") && listeningAverage > 0;
  const readingLevelInfo = hasReadingData ? getTefLevelByScore("Reading", readingAverage) : null;
  const listeningLevelInfo = hasListeningData ? getTefLevelByScore("Listening", listeningAverage) : null;
  
  const readingNclcGoal = userGoals.mode === "same" ? userGoals.globalNclcGoal : userGoals.goals.reading;
  const listeningNclcGoal = userGoals.mode === "same" ? userGoals.globalNclcGoal : userGoals.goals.listening;
  const writingNclcGoal = userGoals.mode === "same" ? userGoals.globalNclcGoal : userGoals.goals.writing;

  const readingGap = getScoreGapToGoal("Reading", readingAverage, readingNclcGoal);
  const listeningGap = getScoreGapToGoal("Listening", listeningAverage, listeningNclcGoal);
  const writingTargetScore = getTargetScoreForNclc("Writing", writingNclcGoal);
  
  // --- DETAILED EE IA STATS CALCULATIONS ---
  let eeScoreSum = 0;
  let eeCount = 0;
  let eeBandMinSum = 0;
  let eeBandMaxSum = 0;
  let writingProductionsCount = 0;
  let eeHighCount = 0;
  let eeLowCount = 0;

  historyItems.forEach(item => {
    if (isWritingV2HistoryRecord(item)) {
      return;
    }
    const isComp = item.test_type === "complete" || item.testType === "complete" || item.mode === "full_exam" || item.competence === "Simulado Completo";
    if (isComp) {
      const ee = item.result?.ee || item.sections?.writing || {};
      const hasEEAnswer = ee.text || ee.answer || item.answers?.ee;
      if (hasEEAnswer) {
        writingProductionsCount++;
        if (ee.status === "corrected" && typeof ee.score === "number" && ee.score > 0) {
          eeScoreSum += ee.score;
          const corr = ee.correction || {};
          const bMin = corr.estimatedBand?.min || corr.scoreConfidenceRange?.min || (ee.score - 25);
          const bMax = corr.estimatedBand?.max || corr.scoreConfidenceRange?.max || (ee.score + 25);
          eeBandMinSum += Math.max(0, bMin);
          eeBandMaxSum += Math.min(450, bMax);
          
          const conf = corr.scoreConfidence || "medium";
          if (conf === "high") eeHighCount++;
          else if (conf === "low") eeLowCount++;
          eeCount++;
        }
      }
    } else if (isWritingItem(item)) {
      writingProductionsCount++;
      const eeScore = resolveWritingScore(item);
      if (eeScore !== null) {
        eeScoreSum += eeScore;
        const corr = item.metadata?.writingCorrection || item.result?.correction || item.writingCorrection || {};
        const bMin = corr.estimatedBand?.min || corr.scoreConfidenceRange?.min || (eeScore - 25);
        const bMax = corr.estimatedBand?.max || corr.scoreConfidenceRange?.max || (eeScore + 25);
        eeBandMinSum += Math.max(0, bMin);
        eeBandMaxSum += Math.min(450, bMax);
        
        const conf = corr.scoreConfidence || "medium";
        if (conf === "high") eeHighCount++;
        else if (conf === "low") eeLowCount++;
        eeCount++;
      }
    }
  });

  const eeScoreAverage = eeCount > 0 ? Math.round(eeScoreSum / eeCount) : 0;
  const eeBandMinAverage = eeCount > 0 ? Math.round(eeBandMinSum / eeCount) : 0;
  const eeBandMaxAverage = eeCount > 0 ? Math.round(eeBandMaxSum / eeCount) : 0;
  
  let eeConfidenceAverage = "Média";
  if (eeCount > 0) {
    if (eeHighCount > eeCount / 2) eeConfidenceAverage = "Alta";
    else if (eeLowCount > eeCount / 2) eeConfidenceAverage = "Baixa";
  }

  const hasWritingCorrections = eeCount > 0;
  const eeLevelInfo = hasWritingCorrections ? getTefLevelByScore("Writing", eeScoreAverage) : null;
  const eeGap = hasWritingCorrections ? getScoreGapToGoal("Writing", eeScoreAverage, writingNclcGoal) : getScoreGapToGoal("Writing", 0, writingNclcGoal);
  
  // --- EE V2 PEDAGOGICAL CALCULATIONS ---
  const eeV2Records = historyItems.filter(isWritingV2HistoryRecord);
  const totalSubmitted = eeV2Records.length;
  const correctedRecords = eeV2Records.filter(r => r.status === "concluido" || r.metadata?.hasAiCorrection === true);
  const totalCorrections = correctedRecords.length;

  let totalErrors = 0;
  let totalWords = 0;
  const categoryCounts: { [key: string]: number } = {};
  
  // Section A specifically
  let secACount = 0;
  let secAWordsSum = 0;
  let secAErrorsSum = 0;
  const secACatCounts: { [key: string]: number } = {};

  // Section B specifically
  let secBCount = 0;
  let secBWordsSum = 0;
  let secBErrorsSum = 0;
  const secBCatCounts: { [key: string]: number } = {};

  eeV2Records.forEach(record => {
    const isMultiTask = record.testType === "complete" || record.test_type === "complete" || record.metadata?.isFullWritingTest === true || record.competence === "Simulado Completo";

    if (isMultiTask) {
      // Process sectionA
      const secA = record.answers?.sectionA || record.result?.ee?.answers?.sectionA || record.metadata?.writingTasks?.find((t: any) => t.section === "Section A");
      if (secA) {
        const wordCount = secA.wordCount || secA.words || 0;
        const errCount = secA.correction?.errors?.length || secA.errorCount || 0;
        
        secAWordsSum += wordCount;
        secAErrorsSum += errCount;
        secACount++;
        totalWords += wordCount;
        totalErrors += errCount;
        
        const errorsList = secA.correction?.errors || [];
        errorsList.forEach((e: any) => {
          const cat = e.category || "Outro";
          categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
          secACatCounts[cat] = (secACatCounts[cat] || 0) + 1;
        });
      }

      // Process sectionB
      const secB = record.answers?.sectionB || record.result?.ee?.answers?.sectionB || record.metadata?.writingTasks?.find((t: any) => t.section === "Section B");
      if (secB) {
        const wordCount = secB.wordCount || secB.words || 0;
        const errCount = secB.correction?.errors?.length || secB.errorCount || 0;
        
        secBWordsSum += wordCount;
        secBErrorsSum += errCount;
        secBCount++;
        totalWords += wordCount;
        totalErrors += errCount;
        
        const errorsList = secB.correction?.errors || [];
        errorsList.forEach((e: any) => {
          const cat = e.category || "Outro";
          categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
          secBCatCounts[cat] = (secBCatCounts[cat] || 0) + 1;
        });
      }
    } else {
      const errCount = getWritingV2ErrorCount(record);
      const wordCount = getWritingV2WordCount(record);
      const sec = getWritingV2Section(record);
      
      totalErrors += errCount;
      totalWords += wordCount;
      
      if (sec === "Section A") {
        secAWordsSum += wordCount;
        secAErrorsSum += errCount;
        secACount++;
      } else if (sec === "Section B") {
        secBWordsSum += wordCount;
        secBErrorsSum += errCount;
        secBCount++;
      }

      const errorsList = getWritingV2Errors(record);
      if (errorsList && errorsList.length > 0) {
        errorsList.forEach(e => {
          const cat = e.category || "Outro";
          categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
          if (sec === "Section A") {
            secACatCounts[cat] = (secACatCounts[cat] || 0) + 1;
          } else if (sec === "Section B") {
            secBCatCounts[cat] = (secBCatCounts[cat] || 0) + 1;
          }
        });
      } else {
        const focusAreas = getWritingV2FocusAreas(record);
        if (focusAreas && focusAreas.length > 0) {
          focusAreas.forEach(cat => {
            categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
            if (sec === "Section A") {
              secACatCounts[cat] = (secACatCounts[cat] || 0) + 1;
            } else if (sec === "Section B") {
              secBCatCounts[cat] = (secBCatCounts[cat] || 0) + 1;
            }
          });
        }
      }
    }
  });

  const averageWords = totalSubmitted > 0 ? Math.round(totalWords / totalSubmitted) : 0;
  
  // Section A statistics
  const secAAverageWords = secACount > 0 ? Math.round(secAWordsSum / secACount) : 0;
  const secAAverageErrors = secACount > 0 ? Math.round(secAErrorsSum / secACount) : 0;
  let secAFrequentCategory = "N/A";
  let maxSecACatVal = 0;
  Object.entries(secACatCounts).forEach(([cat, val]) => {
    if (val > maxSecACatVal) {
      maxSecACatVal = val;
      secAFrequentCategory = cat;
    }
  });

  // Section B statistics
  const secBAverageWords = secBCount > 0 ? Math.round(secBWordsSum / secBCount) : 0;
  const secBAverageErrors = secBCount > 0 ? Math.round(secBErrorsSum / secBCount) : 0;
  let secBFrequentCategory = "N/A";
  let maxSecBCatVal = 0;
  Object.entries(secBCatCounts).forEach(([cat, val]) => {
    if (val > maxSecBCatVal) {
      maxSecBCatVal = val;
      secBFrequentCategory = cat;
    }
  });

  // Sort categories to find the top ones
  const sortedCategories = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([category, count]) => ({ category, count }));

  const topFocusCategory = sortedCategories.length > 0 ? sortedCategories[0].category : "N/A";

  // Count EE V2 study recommended block
  let focusRecomendado = t("dashboard.recommendedStudyFocus.default", "Faça mais correções para receber recomendações personalizadas.");
  if (topFocusCategory !== "N/A") {
    const catLower = topFocusCategory.toLowerCase();
    if (catLower.includes("preposição") || catLower.includes("preposicao") || catLower.includes("preposition")) {
      focusRecomendado = t("dashboard.recommendedStudyFocus.prepositions", "Seu erro mais recorrente está em preposições. Priorize revisar verbos e expressões que exigem à, de, pour, par e en.");
    } else if (catLower.includes("concordância") || catLower.includes("concordancia") || catLower.includes("accord") || catLower.includes("agreement")) {
      focusRecomendado = t("dashboard.recommendedStudyFocus.agreement", "Seu foco principal deve ser concordância de gênero e número, especialmente entre artigos, adjetivos e substantivos.");
    } else if (catLower.includes("conjugação") || catLower.includes("conjugacao") || catLower.includes("conjugaison") || catLower.includes("conjugation")) {
      focusRecomendado = t("dashboard.recommendedStudyFocus.conjugation", "Revise conjugação verbal em tempos usados frequentemente na produção escrita, como présent, passé composé, imparfait e conditionnel.");
    } else if (catLower.includes("ortografia") || catLower.includes("orthographe") || catLower.includes("spelling")) {
      focusRecomendado = t("dashboard.recommendedStudyFocus.spelling", "Revise acentos, terminações e grafia de palavras recorrentes.");
    } else {
      focusRecomendado = t("dashboard.recommendedStudyFocus.fallback", "Seu foco principal deve ser a área de {{category}}. Priorize revisar exemplos práticos e criar mais flashcards a partir dos seus desvios identificados.", { category: translateErrorCategory(topFocusCategory, t) });
    }
  }
  
  // Simulations completed this month
  const now = new Date();
  const simulationsCompletedCount = historyItems.filter(item => {
    try {
      const isComp = item.test_type === "complete" || item.testType === "complete" || item.mode === "full_exam" || item.competence === "Simulado Completo";
      if (!isComp) return false;
      const d = new Date(item.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    } catch {
      return false;
    }
  }).length;

  // Flashcards pending (SRS rules: nextReviewAt is missing or past/present)
  const currentSrsDate = new Date();
  const pendingFlashcardsCount = flashcards.filter(c => !c.nextReviewAt || new Date(c.nextReviewAt) <= currentSrsDate).length;

  // Streak days calculation based on consecutive active days
  const streakDays = streakService.getCurrentStreak();

  // Count EE V2 Flashcards specifically
  const eeV2Flashcards = flashcards.filter(isWritingV2Flashcard);
  const writingV2FlashcardsCount = eeV2Flashcards.length;

  const currentSrsDateVal = new Date();
  const eeV2DueFlashcardsCount = eeV2Flashcards.filter(c => !c.nextReviewAt || new Date(c.nextReviewAt) <= currentSrsDateVal).length;

  // Telemetry log
  console.log("[DASHBOARD_EE_V2_STATS]", {
    totalCorrections,
    totalErrors,
    averageWords,
    sectionAStats: { secACount, secAAverageWords, secAAverageErrors, secAFrequentCategory },
    sectionBStats: { secBCount, secBAverageWords, secBAverageErrors, secBFrequentCategory },
    topCategories: sortedCategories,
    writingV2FlashcardsCount,
  });

  // --- PERFORMANCE BY SECTION CALCULATIONS ---
  const sectionsData: Record<string, {
    name: string;
    competence: "Reading" | "Listening" | "Writing";
    correct: number;
    total: number;
    history: number[];
    lastResult: number | null;
    correctionsCount: number;
    wordsSum: number;
    errorsSum: number;
    errorsFocus: Record<string, number>;
  }> = {};

  const standardReadingSections = [
    "Documents divers",
    "Phrases et textes lacunaires",
    "Lecture rapide de textes et graphiques",
    "Documents administratifs et profissionais",
    "Articles de presse"
  ];

  const standardListeningSections = [
    "Dialogues associés à des images",
    "Annonces publiques",
    "Messages courts / situations téléphoniques",
    "Micro-trottoirs",
    "Chroniques radio",
    "Interviews",
    "Reportages",
    "Documents divers"
  ];

  const standardWritingSections = [
    "Section A",
    "Section B"
  ];

  // Pre-populate standard sections
  standardReadingSections.forEach(sec => {
    sectionsData[`Reading - ${sec}`] = {
      name: sec,
      competence: "Reading",
      correct: 0,
      total: 0,
      history: [],
      lastResult: null,
      correctionsCount: 0,
      wordsSum: 0,
      errorsSum: 0,
      errorsFocus: {}
    };
  });

  standardListeningSections.forEach(sec => {
    sectionsData[`Listening - ${sec}`] = {
      name: sec,
      competence: "Listening",
      correct: 0,
      total: 0,
      history: [],
      lastResult: null,
      correctionsCount: 0,
      wordsSum: 0,
      errorsSum: 0,
      errorsFocus: {}
    };
  });

  standardWritingSections.forEach(sec => {
    sectionsData[`Writing - ${sec}`] = {
      name: sec,
      competence: "Writing",
      correct: 0,
      total: 0,
      history: [],
      lastResult: null,
      correctionsCount: 0,
      wordsSum: 0,
      errorsSum: 0,
      errorsFocus: {}
    };
  });

  function extractScoreDetails(item: any) {
    if (item.score && typeof item.score === "string" && item.score.includes("/")) {
      const parts = item.score.split("/");
      const correct = parseInt(parts[0]);
      const total = parseInt(parts[1]);
      if (!isNaN(correct) && !isNaN(total) && total > 0) {
        return { correct, total };
      }
    }
    const pct = item.percentage ?? (typeof item.score === "number" ? item.score : parseFloat(item.score || "0"));
    if (!isNaN(pct)) {
      const total = 10;
      const correct = Math.round((pct / 100) * total);
      return { correct, total };
    }
    return { correct: 0, total: 0 };
  }

  function calculateTrend(history: number[], isEE: boolean = false): "Improving" | "Stable" | "Declining" | "Not enough data" {
    if (!history || history.length < 2) return "Not enough data";
    const lastAttempts = history.slice(-3);
    if (lastAttempts.length === 2) {
      const diff = lastAttempts[1] - lastAttempts[0];
      if (Math.abs(diff) <= 3) return "Stable";
      return isEE ? (diff < 0 ? "Improving" : "Declining") : (diff > 0 ? "Improving" : "Declining");
    } else {
      const netDiff = lastAttempts[2] - lastAttempts[0];
      if (Math.abs(netDiff) <= 5) return "Stable";
      return isEE ? (netDiff < 0 ? "Improving" : "Declining") : (netDiff > 0 ? "Improving" : "Declining");
    }
  }

  const chronologicalItems = [...historyItems].reverse();

  chronologicalItems.forEach(item => {
    const isComp = item.test_type === "complete" || item.testType === "complete" || item.mode === "full_exam" || item.competence === "Simulado Completo";

    if (isComp) {
      const ceSecResults = item.sections?.reading?.sectionResults || item.result?.ce?.sectionResults || item.sectionResults;
      if (ceSecResults) {
        Object.entries(ceSecResults).forEach(([secName, res]: [string, any]) => {
          const key = `Reading - ${secName}`;
          if (!sectionsData[key]) {
            sectionsData[key] = {
              name: secName,
              competence: "Reading",
              correct: 0,
              total: 0,
              history: [],
              lastResult: null,
              correctionsCount: 0,
              wordsSum: 0,
              errorsSum: 0,
              errorsFocus: {}
            };
          }
          sectionsData[key].correct += res.correct || 0;
          sectionsData[key].total += res.total || 0;
          const accuracy = res.total > 0 ? Math.round((res.correct / res.total) * 100) : 0;
          sectionsData[key].history.push(accuracy);
          sectionsData[key].lastResult = accuracy;
        });
      }

      const coSecResults = item.sections?.listening?.sectionResults || item.result?.co?.sectionResults || item.sectionResults;
      if (coSecResults) {
        Object.entries(coSecResults).forEach(([secName, res]: [string, any]) => {
          const key = `Listening - ${secName}`;
          if (!sectionsData[key]) {
            sectionsData[key] = {
              name: secName,
              competence: "Listening",
              correct: 0,
              total: 0,
              history: [],
              lastResult: null,
              correctionsCount: 0,
              wordsSum: 0,
              errorsSum: 0,
              errorsFocus: {}
            };
          }
          sectionsData[key].correct += res.correct || 0;
          sectionsData[key].total += res.total || 0;
          const accuracy = res.total > 0 ? Math.round((res.correct / res.total) * 100) : 0;
          sectionsData[key].history.push(accuracy);
          sectionsData[key].lastResult = accuracy;
        });
      }
    } else {
      const isReading = isReadingItem(item);
      const isListening = isListeningItem(item);
      const isWriting = isWritingItem(item) || isWritingV2HistoryRecord(item);

      if (isReading || isListening) {
        const comp: "Reading" | "Listening" = isReading ? "Reading" : "Listening";
        const secName = item.selectedSection || item.section || item.metadata?.section || "Documents divers";
        const key = `${comp} - ${secName}`;

        if (!sectionsData[key]) {
          sectionsData[key] = {
            name: secName,
            competence: comp,
            correct: 0,
            total: 0,
            history: [],
            lastResult: null,
            correctionsCount: 0,
            wordsSum: 0,
            errorsSum: 0,
            errorsFocus: {}
          };
        }

        const scoreDetails = extractScoreDetails(item);
        sectionsData[key].correct += scoreDetails.correct;
        sectionsData[key].total += scoreDetails.total;
        const accuracy = scoreDetails.total > 0 ? Math.round((scoreDetails.correct / scoreDetails.total) * 100) : 0;
        sectionsData[key].history.push(accuracy);
        sectionsData[key].lastResult = accuracy;
      } else if (isWriting) {
        const isV2 = isWritingV2HistoryRecord(item);
        const secName = isV2 ? getWritingV2Section(item) : (item.selectedSection || item.section || "Section A");
        const key = `Writing - ${secName}`;

        if (!sectionsData[key]) {
          sectionsData[key] = {
            name: secName,
            competence: "Writing",
            correct: 0,
            total: 0,
            history: [],
            lastResult: null,
            correctionsCount: 0,
            wordsSum: 0,
            errorsSum: 0,
            errorsFocus: {}
          };
        }

        const words = isV2 ? getWritingV2WordCount(item) : (item.words || item.sections?.writing?.words || 0);
        const errorsCount = isV2 ? getWritingV2ErrorCount(item) : (item.metadata?.writingCorrection?.errors?.length || 0);

        sectionsData[key].correctionsCount += 1;
        sectionsData[key].wordsSum += words;
        sectionsData[key].errorsSum += errorsCount;
        sectionsData[key].history.push(errorsCount);
        sectionsData[key].lastResult = errorsCount;

        const errorsList = isV2 ? getWritingV2Errors(item) : (item.metadata?.writingCorrection?.errors || []);
        if (errorsList && errorsList.length > 0) {
          errorsList.forEach((e: any) => {
            const cat = e.category || "Ortografia";
            sectionsData[key].errorsFocus[cat] = (sectionsData[key].errorsFocus[cat] || 0) + 1;
          });
        }
      }
    }
  });

  const renderedSections = Object.values(sectionsData).map(sec => {
    const hasData = sec.competence === "Writing" ? sec.correctionsCount > 0 : sec.total > 0;
    let status: "Strong" | "On Track" | "Needs Attention" | "Critical" = "Needs Attention";
    let action = "";
    let accuracy = 0;
    let avgWords = 0;
    let avgErrors = 0;
    let mainErrorFocus = "N/A";
    
    if (sec.competence === "Writing") {
      avgWords = sec.correctionsCount > 0 ? Math.round(sec.wordsSum / sec.correctionsCount) : 0;
      avgErrors = sec.correctionsCount > 0 ? Math.round(sec.errorsSum / sec.correctionsCount) : 0;
      
      let maxCatCount = 0;
      Object.entries(sec.errorsFocus).forEach(([cat, val]) => {
        if (val > maxCatCount) {
          maxCatCount = val;
          mainErrorFocus = cat;
        }
      });
      
      if (avgErrors <= 3) status = "Strong";
      else if (avgErrors <= 7) status = "On Track";
      else if (avgErrors <= 12) status = "Needs Attention";
      else status = "Critical";
      
      action = "Practice Writing Section";
    } else {
      accuracy = sec.total > 0 ? Math.round((sec.correct / sec.total) * 100) : 0;
      
      if (accuracy >= 85) status = "Strong";
      else if (accuracy >= 70) status = "On Track";
      else if (accuracy >= 50) status = "Needs Attention";
      else status = "Critical";
      
      action = sec.competence === "Reading" ? "Practice Reading Section" : "Practice Listening Section";
    }
    
    const trend = calculateTrend(sec.history, sec.competence === "Writing");
    
    return {
      ...sec,
      hasData,
      accuracy,
      avgWords,
      avgErrors,
      mainErrorFocus,
      status,
      trend,
      action
    };
  });

  const activeSectionsWithData = renderedSections.filter(s => s.hasData);
  let weakestSection: any = null;

  if (activeSectionsWithData.length > 0) {
    weakestSection = [...activeSectionsWithData].sort((a, b) => {
      const scoreA = a.competence === "Writing" ? Math.max(0, 100 - (a.avgErrors * 10)) : a.accuracy;
      const scoreB = b.competence === "Writing" ? Math.max(0, 100 - (b.avgErrors * 10)) : b.accuracy;
      return scoreA - scoreB;
    })[0];
  }

  // --- WEAKNESS MAP CALCULATIONS ---
  // CE/CO section weaknesses
  const sectionWeaknesses = renderedSections.filter(s => s.hasData).map(s => {
    let severity: "Strong" | "On Track" | "Needs Attention" | "Critical" = s.status;
    let severityNum = 1;
    if (severity === "Critical") severityNum = 4;
    else if (severity === "Needs Attention") severityNum = 3;
    else if (severity === "On Track") severityNum = 2;

    let reason = "";
    if (s.competence === "Writing") {
      reason = s.status === "Critical" 
        ? t("weaknessMap.reasons.writingCritical", "Avg. errors: {{avgErrors}} per essay (Critical)", { avgErrors: s.avgErrors })
        : s.status === "Needs Attention"
        ? t("weaknessMap.reasons.writingNeedsAttention", "Avg. errors: {{avgErrors}} per essay (Needs Attention)", { avgErrors: s.avgErrors })
        : s.status === "On Track"
        ? t("weaknessMap.reasons.writingOnTrack", "Avg. errors near target ({{avgErrors}})", { avgErrors: s.avgErrors })
        : t("weaknessMap.reasons.writingStrong", "Excellent control ({{avgErrors}} errors avg)", { avgErrors: s.avgErrors });
    } else {
      reason = s.accuracy < 50
        ? t("weaknessMap.reasons.readingCritical", "Accuracy below 50% ({{accuracy}}%)", { accuracy: s.accuracy })
        : s.accuracy <= 69
        ? t("weaknessMap.reasons.readingNeedsAttention", "Accuracy below target ({{accuracy}}%)", { accuracy: s.accuracy })
        : s.accuracy <= 84
        ? t("weaknessMap.reasons.readingOnTrack", "Accuracy near target ({{accuracy}}%)", { accuracy: s.accuracy })
        : t("weaknessMap.reasons.readingStrong", "Excellent accuracy ({{accuracy}}%)", { accuracy: s.accuracy });
    }

    let actionLabel = t("weaknessMap.actions.practiceSection", "Practice");
    let actionPath = s.competence === "Reading" ? "/start-test?competence=reading" : 
                     s.competence === "Listening" ? "/start-test?competence=listening" : 
                     "/start-test?competence=writing";

    return {
      id: `section-${s.competence}-${s.name}`,
      name: s.name,
      competence: s.competence,
      severity,
      severityNum,
      reason,
      actionLabel,
      actionPath,
      volume: s.competence === "Writing" ? s.errorsSum : s.total,
      type: "section" as const
    };
  });

  // Writing error categories from pedagogical corrections & Error Bank
  const combinedWritingCategories: Record<string, { count: number; lastModified?: string }> = {};

  // 1. Pedagogical corrections categories
  Object.entries(categoryCounts).forEach(([cat, val]) => {
    combinedWritingCategories[cat] = { count: val };
  });

  // 2. Error Bank categories
  writingErrors.forEach((e: any) => {
    const cat = (e.category || "Ortografia").trim();
    const date = e.savedAt || e.createdAt || e.date;
    if (!combinedWritingCategories[cat]) {
      combinedWritingCategories[cat] = { count: 0 };
    }
    combinedWritingCategories[cat].count += 1;
    if (date) {
      if (!combinedWritingCategories[cat].lastModified || new Date(date) > new Date(combinedWritingCategories[cat].lastModified!)) {
        combinedWritingCategories[cat].lastModified = date;
      }
    }
  });

  const errorCategoryWeaknesses = Object.entries(combinedWritingCategories).map(([catName, data]) => {
    const count = data.count;
    let severity: "Strong" | "On Track" | "Needs Attention" | "Critical" = "Strong";
    let severityNum = 1;
    
    if (count > 8) {
      severity = "Critical";
      severityNum = 4;
    } else if (count >= 4) {
      severity = "Needs Attention";
      severityNum = 3;
    } else if (count >= 1) {
      severity = "On Track";
      severityNum = 2;
    }

    const reason = t("weaknessMap.reasons.writingCategory", "Recurrent writing issue ({{count}} occurrences)", { count });
    const actionLabel = t("weaknessMap.actions.reviewErrorBank", "Review Error Bank");
    const actionPath = "/writing-errors";

    return {
      id: `writing-category-${catName}`,
      name: catName,
      competence: "Writing" as const,
      severity,
      severityNum,
      reason,
      actionLabel,
      actionPath,
      volume: count,
      type: "writing-category" as const,
      lastModified: data.lastModified
    };
  });

  // Pending Flashcards
  const flashcardsWeakness = (() => {
    if (pendingFlashcardsCount === 0) return null;
    
    let severity: "Strong" | "On Track" | "Needs Attention" | "Critical" = "On Track";
    let severityNum = 2;
    
    if (pendingFlashcardsCount > 25) {
      severity = "Critical";
      severityNum = 4;
    } else if (pendingFlashcardsCount > 10) {
      severity = "Needs Attention";
      severityNum = 3;
    }

    const reason = t("weaknessMap.reasons.flashcardsPending", "{{count}} flashcards due for review", { count: pendingFlashcardsCount });
    const actionLabel = t("weaknessMap.actions.studyNow", "Review Flashcards");
    const actionPath = "/flashcards";

    return {
      id: "flashcards-pending",
      name: t("weaknessMap.categories.flashcards", "Vocabulary & Flashcards"),
      competence: "Writing" as const,
      severity,
      severityNum,
      reason,
      actionLabel,
      actionPath,
      volume: pendingFlashcardsCount,
      type: "flashcards" as const
    };
  })();

  // Simulated Exams target gap
  const simulationsWeakness = (() => {
    const parseNclcToNumber = (nclcStr: string | number | undefined): number => {
      if (nclcStr === undefined || nclcStr === null) return 0;
      if (typeof nclcStr === "number") return nclcStr;
      const str = String(nclcStr).trim();
      if (str.includes("<")) return 2;
      if (str.includes("+")) return 10;
      const parsed = parseInt(str);
      return isNaN(parsed) ? 0 : parsed;
    };

    const targetCeNclc = personalGoals?.targetCeNclc ?? parseNclcToNumber(readingNclcGoal) ?? 7;
    const targetCoNclc = personalGoals?.targetCoNclc ?? parseNclcToNumber(listeningNclcGoal) ?? 7;
    const targetEeNclc = personalGoals?.targetEeNclc ?? parseNclcToNumber(writingNclcGoal) ?? 7;

    const currentCeNclc = readingLevelInfo ? parseNclcToNumber(readingLevelInfo.nclc) : 0;
    const currentCoNclc = listeningLevelInfo ? parseNclcToNumber(listeningLevelInfo.nclc) : 0;
    const currentEeNclc = eeLevelInfo ? parseNclcToNumber(eeLevelInfo.nclc) : 0;

    const readingNclcGap = Math.max(0, targetCeNclc - currentCeNclc);
    const listeningNclcGap = Math.max(0, targetCoNclc - currentCoNclc);
    const eeNclcGap = Math.max(0, targetEeNclc - currentEeNclc);

    const maxGap = Math.max(0, readingNclcGap, listeningNclcGap, eeNclcGap);
    if (maxGap === 0 && !hasReadingData && !hasListeningData) return null;
    
    let severity: "Strong" | "On Track" | "Needs Attention" | "Critical" = "Strong";
    let severityNum = 1;
    
    if (maxGap >= 3) {
      severity = "Critical";
      severityNum = 4;
    } else if (maxGap === 2) {
      severity = "Needs Attention";
      severityNum = 3;
    } else if (maxGap === 1) {
      severity = "On Track";
      severityNum = 2;
    } else if (!hasReadingData || !hasListeningData) {
      severity = "Needs Attention";
      severityNum = 3;
    }

    const reason = (!hasReadingData || !hasListeningData)
      ? t("weaknessMap.reasons.simulationsNoData", "Complete a full simulation to calibrate target goals")
      : t("weaknessMap.reasons.simulationsGap", "NCLC target gap of {{gap}} points", { gap: maxGap });
    const actionLabel = t("weaknessMap.actions.takeSimulation", "Take Full Simulation");
    const actionPath = "/start-test";

    return {
      id: "simulations-gap",
      name: t("weaknessMap.categories.simulations", "Simulated Exams"),
      competence: "General" as const,
      severity,
      severityNum,
      reason,
      actionLabel,
      actionPath,
      volume: maxGap,
      type: "simulations" as const
    };
  })();

  const allWeaknesses: any[] = [
    ...sectionWeaknesses,
    ...errorCategoryWeaknesses,
    ...(flashcardsWeakness ? [flashcardsWeakness] : []),
    ...(simulationsWeakness ? [simulationsWeakness] : [])
  ];

  const activeWeaknesses = allWeaknesses
    .filter(w => w.severity !== "Strong")
    .sort((a, b) => {
      // 1. Sort by severityNum desc (Critical=4, Needs Attention=3, On Track=2)
      if (b.severityNum !== a.severityNum) {
        return b.severityNum - a.severityNum;
      }
      // 2. Sort by volume desc
      if (b.volume !== a.volume) {
        return b.volume - a.volume;
      }
      // 3. Sort by lastModified/recent date desc if present
      if (a.lastModified || b.lastModified) {
        const timeA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
        const timeB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
        return timeB - timeA;
      }
      return 0;
    })
    .slice(0, 5);

  const hasEnoughDataForWeaknessMap = historyItems.length > 0 || flashcards.length > 0 || writingErrors.length > 0;

  // --- TODAY'S STUDY PLAN TASKS GENERATION ---
  const getTaskProgress = (plan: any) => {
    const estTimeParts = plan.estimatedTime.split(" ");
    const estMinutes = parseInt(estTimeParts[0]) || 15;
    
    let studiedSecs = 0;
    const path = (plan.actionPath || "").toLowerCase();
    const title = (plan.title || "").toLowerCase();
    
    if (path.includes("reading") || title.includes("reading") || title.includes("ce")) {
      studiedSecs = studyTimeByCompetency.reading;
    } else if (path.includes("listening") || title.includes("listening") || title.includes("co")) {
      studiedSecs = studyTimeByCompetency.listening;
    } else if (path.includes("writing") || title.includes("writing") || title.includes("ee")) {
      studiedSecs = studyTimeByCompetency.writing;
    } else if (path.includes("flashcards") || title.includes("flashcard")) {
      studiedSecs = studyTimeByCompetency.flashcards;
    } else if (path.includes("errors") || path.includes("writing-errors") || title.includes("error")) {
      studiedSecs = studyTimeByCompetency.errorBank;
    } else if (path.includes("history") || title.includes("history")) {
      studiedSecs = studyTimeByCompetency.general;
    } else {
      studiedSecs = studyTimeByCompetency.general;
    }
    
    const studiedMinutes = studiedSecs / 60;
    const progressPercent = Math.min(100, Math.round((studiedMinutes / estMinutes) * 100));
    
    return {
      progressPercent,
      studiedMinutes: Math.round(studiedMinutes),
      estMinutes
    };
  };

  const parseNclcToNumberVal = (nclcStr: string | number | undefined): number => {

    if (nclcStr === undefined || nclcStr === null) return 0;
    if (typeof nclcStr === "number") return nclcStr;
    const str = String(nclcStr).trim();
    if (str.includes("<")) return 2;
    if (str.includes("+")) return 10;
    const parsed = parseInt(str);
    return isNaN(parsed) ? 0 : parsed;
  };

  const targetCeNclcVal = personalGoals?.targetCeNclc ?? parseNclcToNumberVal(readingNclcGoal) ?? 7;
  const targetCoNclcVal = personalGoals?.targetCoNclc ?? parseNclcToNumberVal(listeningNclcGoal) ?? 7;
  const targetEeNclcVal = personalGoals?.targetEeNclc ?? parseNclcToNumberVal(writingNclcGoal) ?? 7;
  const currentCeNclcVal = readingLevelInfo ? parseNclcToNumberVal(readingLevelInfo.nclc) : 0;
  const currentCoNclcVal = listeningLevelInfo ? parseNclcToNumberVal(listeningLevelInfo.nclc) : 0;
  const currentEeNclcVal = eeLevelInfo ? parseNclcToNumberVal(eeLevelInfo.nclc) : 0;

  const allStudyCandidates: any[] = [];

  // Prio 1: top weakness
  if (activeWeaknesses.length > 0) {
    const topWeak = activeWeaknesses[0];
    let topWeakActionLabel = topWeak.actionLabel;
    let topWeakTitle = "";
    let topWeakDesc = topWeak.reason;
    let topWeakEst = "15 min";
    
    if (topWeak.type === "section") {
      topWeakTitle = `${t("performanceBySection.practiceThisSection", "Practice")} ${topWeak.name}`;
      topWeakActionLabel = t("weaknessMap.todayStudyPlan.startPractice", "Start Practice");
      topWeakEst = topWeak.competence === "Listening" ? "15 min" : (topWeak.name.includes("Section B") ? "35 min" : (topWeak.name.includes("Section A") ? "25 min" : "20 min"));
    } else if (topWeak.type === "writing-category") {
      topWeakTitle = `${t("weaknessMap.actions.reviewErrorBank", "Review Error Bank")}: ${topWeak.name}`;
      topWeakActionLabel = t("weaknessMap.todayStudyPlan.reviewErrors", "Review Errors");
      topWeakEst = "10 min";
    } else if (topWeak.type === "flashcards") {
      topWeakTitle = t("weaknessMap.actions.studyNow", "Review Flashcards");
      topWeakActionLabel = t("weaknessMap.todayStudyPlan.studyNow", "Study Now");
      topWeakEst = "15 min";
    } else {
      topWeakTitle = t("weaknessMap.actions.takeSimulation", "Take Full Simulation");
      topWeakActionLabel = t("weaknessMap.todayStudyPlan.startSimulationAction", "Start Simulation");
      topWeakEst = "60 min";
    }
    
    allStudyCandidates.push({
      id: `prio1-weakness-${topWeak.id}`,
      title: topWeakTitle,
      description: topWeakDesc,
      estimatedTime: topWeakEst,
      actionLabel: topWeakActionLabel,
      actionPath: topWeak.actionPath,
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 1`
    });
  }

  // Prio 2: CE/CO below target
  if (hasReadingData && currentCeNclcVal < targetCeNclcVal) {
    allStudyCandidates.push({
      id: "prio2-reading-gap",
      title: t("weaknessMap.todayStudyPlan.practiceReading", "Practice Reading"),
      description: t("weaknessMap.todayStudyPlan.readingGapDesc", "Your CE NCLC is below your target goal. Practice reading to improve."),
      estimatedTime: "20 min",
      actionLabel: t("weaknessMap.todayStudyPlan.startPractice", "Start Practice"),
      actionPath: "/start-test?competence=reading",
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 2`
    });
  }
  if (hasListeningData && currentCoNclcVal < targetCoNclcVal) {
    allStudyCandidates.push({
      id: "prio2-listening-gap",
      title: t("weaknessMap.todayStudyPlan.practiceListening", "Practice Listening"),
      description: t("weaknessMap.todayStudyPlan.listeningGapDesc", "Your CO NCLC is below your target goal. Practice listening to improve."),
      estimatedTime: "15 min",
      actionLabel: t("weaknessMap.todayStudyPlan.startPractice", "Start Practice"),
      actionPath: "/start-test?competence=listening",
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 2`
    });
  }

  // Prio 3: EE score gap or low corrections
  if (eeCount > 0 && eeScoreAverage < writingTargetScore) {
    allStudyCandidates.push({
      id: "prio3-writing-score-gap",
      title: t("weaknessMap.todayStudyPlan.startWriting", "Start Writing Practice"),
      description: t("weaknessMap.todayStudyPlan.writingGapDesc", "Your average writing score is below target. Write an essay to receive pedagogical feedback."),
      estimatedTime: "35 min",
      actionLabel: t("weaknessMap.todayStudyPlan.startWriting", "Start Writing"),
      actionPath: "/start-test?competence=writing",
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 3`
    });
  } else if (totalCorrections < 3) {
    allStudyCandidates.push({
      id: "prio3-writing-corrections-count",
      title: t("weaknessMap.todayStudyPlan.writeSectionAEssay", "Write one Section A essay"),
      description: t("weaknessMap.todayStudyPlan.fewCorrectionsDesc", "You have few registered writing corrections. Keep building your writing history."),
      estimatedTime: "25 min",
      actionLabel: t("weaknessMap.todayStudyPlan.startWriting", "Start Writing"),
      actionPath: "/start-test?competence=writing",
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 3`
    });
  }

  // Prio 4: Pending flashcards
  if (pendingFlashcardsCount > 0) {
    allStudyCandidates.push({
      id: "prio4-flashcards-pending",
      title: t("weaknessMap.todayStudyPlan.studyFlashcards", "Study Flashcards"),
      description: t("weaknessMap.todayStudyPlan.flashcardsDueDesc", "{{count}} cards are due today.", { count: pendingFlashcardsCount }),
      estimatedTime: "15 min",
      actionLabel: t("weaknessMap.todayStudyPlan.studyNow", "Study Now"),
      actionPath: "/flashcards",
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 4`
    });
  }

  // Prio 5: Error Bank review
  if (writingErrors.length > 0) {
    allStudyCandidates.push({
      id: "prio5-error-bank",
      title: t("weaknessMap.todayStudyPlan.reviewErrorBank", "Review Error Bank"),
      description: t("weaknessMap.todayStudyPlan.errorsRegisteredDesc", "You have recurring writing errors registered. Review them to avoid repeating mistakes."),
      estimatedTime: "10 min",
      actionLabel: t("weaknessMap.todayStudyPlan.reviewErrors", "Review Errors"),
      actionPath: "/writing-errors",
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 5`
    });
  }

  // Deduplicate and pad with fallbacks
  const uniqueStudyTasks: any[] = [];
  const seenPaths = new Set<string>();
  allStudyCandidates.forEach(task => {
    if (!seenPaths.has(task.actionPath)) {
      seenPaths.add(task.actionPath);
      uniqueStudyTasks.push(task);
    }
  });

  // Fallbacks
  const fallbackTasks = [
    {
      id: "prio6-history-review",
      title: t("weaknessMap.todayStudyPlan.reviewHistory", "Review History"),
      description: t("weaknessMap.todayStudyPlan.historyDesc", "Check your performance evolution and review previous tests."),
      estimatedTime: "10 min",
      actionLabel: t("weaknessMap.todayStudyPlan.reviewHistoryAction", "View History"),
      actionPath: "/history",
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 6`
    },
    {
      id: "prio6-simulation-maintenance",
      title: t("weaknessMap.todayStudyPlan.takeShortSimulation", "Take Short Simulation"),
      description: t("weaknessMap.todayStudyPlan.simulationStaminaDesc", "Maintain your exam stamina by running a quick diagnostic or simulated exam."),
      estimatedTime: "60 min",
      actionLabel: t("weaknessMap.todayStudyPlan.startSimulationAction", "Start Simulation"),
      actionPath: "/start-test",
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 6`
    },
    {
      id: "prio6-flashcards-explore",
      title: t("weaknessMap.todayStudyPlan.exploreFlashcards", "Explore Vocab & Flashcards"),
      description: t("weaknessMap.todayStudyPlan.exploreCardsDesc", "Explore the flashcard deck to learn new words and connectors."),
      estimatedTime: "15 min",
      actionLabel: t("weaknessMap.todayStudyPlan.studyNow", "Study Now"),
      actionPath: "/flashcards",
      priorityLabel: `${t("weaknessMap.todayStudyPlan.priority", "Priority")} 6`
    }
  ];

  fallbackTasks.forEach(task => {
    if (uniqueStudyTasks.length < 5 && !seenPaths.has(task.actionPath)) {
      seenPaths.add(task.actionPath);
      uniqueStudyTasks.push(task);
    }
  });

  const dailyStudyPlanTasks = uniqueStudyTasks.slice(0, 5);

  // --- CHARTS CALCULATIONS ---
  
  // 1. Progress Data (Evolução de Notas) - last 10 MCQ/Full exam attempts chronologically
  const progressData = [...historyItems]
    .filter(item => isReadingItem(item) || isListeningItem(item) || item.mode === "full_exam" || item.competence === "Simulado Completo")
    .reverse() // from oldest to newest
    .slice(-10) // last 10
    .map((item, idx) => {
      let rScore = undefined;
      let lScore = undefined;
      
      if (item.mode === "full_exam" || item.competence === "Simulado Completo") {
        const ce = item.result?.ce || item.sections?.reading || {};
        const co = item.result?.co || item.sections?.listening || {};
        
        const rPct = ce.percentage ?? ce.percent ?? ce.percentScore ?? 0;
        rScore = Math.round((rPct / 100) * 300);
        
        const lPct = co.percentage ?? co.percent ?? co.percentScore ?? 0;
        lScore = Math.round((lPct / 100) * 360);
      } else if (isReadingItem(item)) {
        const pct = parseScorePercentage(item.score, item.percentage, item.competence);
        rScore = Math.round((pct / 100) * 300);
      } else if (isListeningItem(item)) {
        const pct = parseScorePercentage(item.score, item.percentage, item.competence);
        lScore = Math.round((pct / 100) * 360);
      }
      
      let label = `Sim. ${idx + 1}`;
      try {
        const d = new Date(item.date);
        label = `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
      } catch {}
      
      return {
        id: item.id,
        name: label,
        date: item.date,
        competency: item.mode === "full_exam" || item.competence === "Simulado Completo" ? "Simulado Completo" : item.competence,
        score: item.score,
        percentage: item.percentage || (item.mode === "full_exam" ? 100 : parseScorePercentage(item.score, undefined, item.competence)),
        historyItem: item,
        reading: rScore,
        listening: lScore,
        readingHistoryId: item.mode === "full_exam" || item.competence === "Simulado Completo" || isReadingItem(item) ? item.id : undefined,
        listeningHistoryId: item.mode === "full_exam" || item.competence === "Simulado Completo" || isListeningItem(item) ? item.id : undefined
      };
    });

  // Dynamic YAxis Max calculation based on actually plotted scores
  const plottedValues = progressData.flatMap(item => [
    item.reading,
    item.listening
  ]).filter((value): value is number => typeof value === "number" && Number.isFinite(value));

  const maxPlottedValue = plottedValues.length > 0 ? Math.max(...plottedValues) : 0;
  const yAxisMax = Math.max(360, Math.ceil((maxPlottedValue + 60) / 10) * 10);

  console.log("[ScoreChart] progressData", progressData);
  console.log("[ScoreChart] plottedValues", plottedValues);
  console.log("[ScoreChart] maxPlottedValue", maxPlottedValue);
  console.log("[ScoreChart] yAxisMax", yAxisMax);

  // 2. Skills Radar
  const readingAvgPct = parseScorePercentage(readingAverage / 300 * 100);
  const listeningAvgPct = parseScorePercentage(listeningAverage / 360 * 100);
  
  const writingItems = historyItems.filter(h => isWritingItem(h) || h.sections?.writing);
  let writingAvgPct = 0;
  if (writingItems.length > 0) {
    let totalWords = 0;
    writingItems.forEach(w => {
      const words = w.words || w.sections?.writing?.words || w.metadata?.wordCount || 0;
      totalWords += words;
    });
    const avgWords = totalWords / writingItems.length;
    writingAvgPct = Math.min(100, Math.round((avgWords / 150) * 100)); // normalized against standard 150 words
  }

  const skillsRadar = [
    { subject: "CE", A: readingAvgPct || 0, fullMark: 100 },
    { subject: "CO", A: listeningAvgPct || 0, fullMark: 100 },
    { subject: "EE", A: writingAvgPct || 0, fullMark: 100 }
  ];

  // 3. Flashcards Donut Chart
  const easyCount = flashcards.filter(c => c.difficulty === "easy" || c.difficulty === "fácil").length;
  const mediumCount = flashcards.filter(c => c.difficulty === "medium" || c.difficulty === "médio").length;
  const hardCount = flashcards.filter(c => c.difficulty === "hard" || c.difficulty === "difícil").length;
  const reviewCount = flashcards.filter(c => c.difficulty === "review" || c.difficulty === "em revisão" || c.difficulty === "revisar").length;
  const newCount = flashcards.filter(c => c.difficulty === "new" || c.difficulty === "novo" || c.difficulty === "novos").length;
  
  const flashcardsDonut = [
    { name: t("flashcards.easy", "Easy"), value: easyCount, fill: "#10b981", key: "easy" },
    { name: t("flashcards.medium", "Medium"), value: mediumCount, fill: "#f59e0b", key: "medium" },
    { name: t("flashcards.hard", "Hard"), value: hardCount, fill: "#ef4444", key: "hard" },
    { name: t("flashcards.filterTitle", "In review"), value: reviewCount, fill: "#3b82f6", key: "review" },
    { name: t("common.default", "New"), value: newCount, fill: "#8b5cf6", key: "new" }
  ].filter(item => item.value > 0);

  const hasFlashcardsData = flashcardsDonut.length > 0;

  // 4. Writing Errors grouping by Category
  const categories = ["Concordância", "Preposições", "Conjugação", "Vocabulário", "Ortografia", "Léxico", "Estilo"];
  const errorsByCategory = categories.map(cat => {
    const count = writingErrors.filter((e: any) => {
      const ec = (e.category || "").trim().toLowerCase();
      const target = cat.toLowerCase();
      if (ec === target) return true;
      // Aliases
      if (target === "concordância" && (ec === "concordancia" || ec === "gramática" || ec === "gramatica")) return true;
      if (target === "conjugação" && ec === "conjugacao") return true;
      if (target === "vocabulário" && (ec === "vocabulario" || ec === "vocabulary")) return true;
      if (target === "ortografia" && ec === "orthographe") return true;
      return false;
    }).length;
    return {
      name: cat,
      count
    };
  });
  const hasWritingErrors = errorsByCategory.some(item => item.count > 0);

  // 5. Recent Simulations (last 4 items)
  const recentSimulations = historyItems.slice(0, 4).map(item => {
    let scoreVal: string | null = null;
    let totalVal: number | null = 100;
    const isV2 = item.metadata?.type === "writing_linguistic_correction" && item.metadata?.writingVersion === 2;
    let isEssay = item.competence === "Writing" || item.competence === "Expression Écrite (EE)" || isV2;
    let isFull = item.mode === "full_exam";

    if (isFull) {
      scoreVal = formatCompactScore(item.score);
      totalVal = null;
    } else if (isV2) {
      scoreVal = t("history.pedagogicalCorrection", "Correção pedagógica");
      totalVal = null;
    } else if (isEssay) {
      const eeScore = resolveWritingScore(item);
      console.log("[Dashboard EE Score Resolved]", { id: item.id, eeScore });
      
      if (eeScore !== null) {
        scoreVal = String(eeScore);
        totalVal = resolveWritingMaxScore(item);
      } else {
        scoreVal = t("history.pendingEe", "Pendente");
        totalVal = null;
      }
    } else {
      if (item.score && typeof item.score === "string" && item.score.includes("/")) {
        const match = item.score.match(/(\d+)\s*\/\s*(\d+)/);
        if (match) {
          scoreVal = match[1];
          totalVal = parseInt(match[2]);
        } else {
          scoreVal = item.score;
          totalVal = null;
        }
      } else {
        scoreVal = item.score !== null && item.score !== undefined ? String(item.score) : "0";
        totalVal = item.maxScore || item.max_score || 100;
      }
    }

    let dateLabel = t("history.recently", "Recentemente");
    try {
      const d = new Date(item.date || item.created_at);
      dateLabel = d.toLocaleDateString(i18n.language, { day: "2-digit", month: "2-digit" });
    } catch {}

    return {
      id: item.id,
      type: isFull ? t("history.sectionDisplay.fullSimulated", "Simulado Completo") : `${translateCompetencyLabel(item.competence, t)} - ${getSectionDisplay(item, t)}`,
      date: dateLabel,
      score: scoreVal,
      total: totalVal,
      level: isV2 ? t("history.pedagogicalCorrection", "Correção pedagógica") : isEssay ? (resolveWritingScore(item) !== null ? t("history.corrected", "Corrigido") : t("history.pendingEe", "Pendente")) : null
    };
  });

  // Helper render for Empty Chart Message
  const renderEmptyChartPlaceholder = () => (
    <div className="flex flex-col items-center justify-center h-full text-center p-6 bg-gray-50/30 rounded-2xl border border-dashed border-gray-100">
      <Sparkles className="w-8 h-8 mb-2 stroke-[1.2] text-gray-300 animate-pulse" />
      <span className="text-xs font-bold text-gray-500">{t("dashboard.completeTestsToGenerateSummary")}</span>
      <span className="text-[10px] text-gray-400 mt-0.5 leading-relaxed max-w-[200px]">
        {t("dashboard.noActivityDesc")}
      </span>
    </div>
  );

  const generateTefCoachInsight = () => {
    const name = profile?.name || user?.email?.split("@")[0] || t("common.student", "Estudante");
    
    // Gap and targets values
    const targetCe = personalGoals?.targetCeNclc ?? 7;
    const targetCo = personalGoals?.targetCoNclc ?? 7;
    const currentCe = readingLevelInfo ? parseInt(readingLevelInfo.nclc) || 0 : 0;
    const currentCo = listeningLevelInfo ? parseInt(listeningLevelInfo.nclc) || 0 : 0;
    
    // Parágrafo 1: Visão Geral e Saúde do Estudo
    let p1 = "";
    if (currentCe >= targetCe && currentCo >= targetCo) {
      p1 = t("coach.p1.success", "Olá, {{name}}! Analisei seus dados de progresso e notei que você tem demonstrado uma dedicação consistente na plataforma. Parabéns! Seus níveis atuais em Compréhension Écrite (NCLC {{currentCe}}) e Compréhension Orale (NCLC {{currentCo}}) já atingiram ou superaram suas metas de estudo. Continue nesse ritmo para manter a fluidez e a agilidade nas respostas.", { name, currentCe, currentCo });
    } else if (currentCe >= targetCe) {
      p1 = t("coach.p1.readingSuccess", "Olá, {{name}}! Analisei seus dados de progresso e notei que você tem demonstrado uma dedicação consistente na plataforma. Você está muito bem na Compréhension Écrite, onde já atingiu o nível NCLC {{currentCe}}. No entanto, ainda há trabalho a fazer na Compréhension Orale para sincronizar suas habilidades com sua meta final de proficiência canadense.", { name, currentCe });
    } else if (currentCo >= targetCo) {
      p1 = t("coach.p1.listeningSuccess", "Olá, {{name}}! Analisei seus dados de progresso e notei que você tem demonstrado uma dedicação consistente na plataforma. Excelente trabalho na Compréhension Orale, onde seu nível atual é NCLC {{currentCo}}, alinhado aos seus objetivos. Agora, nosso foco principal deve ser elevar seu desempenho na leitura para garantir que a Compréhension Écrite não seja um obstáculo.", { name, currentCo });
    } else {
      p1 = t("coach.p1.belowTarget", "Olá, {{name}}! Analisei seus dados de progresso e notei que você tem demonstrado uma dedicação consistente na plataforma. Atualmente, há um espaço importante a ser preenchido para alcançar seus objetivos de imigração canadense. Suas médias em leitura (NCLC {{currentCe}}) e escuta (NCLC {{currentCo}}) estão abaixo da meta ideal (NCLC {{targetCe}}/{{targetCo}}), exigindo foco direcionado nesta fase de preparação.", { name, currentCe, currentCo, targetCe, targetCo });
    }

    // Parágrafo 2: Análise de CE/CO e Lacunas
    let p2 = "";
    if (activeWeaknesses.length > 0 && activeWeaknesses.some(w => w.competence === "Reading" || w.competence === "Listening")) {
      const topObjectiveWeak = activeWeaknesses.find(w => w.competence === "Reading" || w.competence === "Listening");
      const compName = topObjectiveWeak?.competence === "Reading" ? t("coach.reading", "Leitura") : t("coach.listening", "Escuta");
      const sectionName = topObjectiveWeak?.name;
      p2 = t("coach.p2.weaknessFound", "Aprofundando nas seções objetivas, identificamos que a competência de {{compName}} apresenta os maiores pontos de atenção. Especificamente na seção \"{{sectionName}}\", sua precisão atual está abaixo da média ideal. Sugiro praticar testes específicos dessa seção para consolidar a mecânica de interpretação sob o tempo de prova.", { compName, sectionName });
    } else {
      const ceGap = Math.max(0, targetCe - currentCe);
      const coGap = Math.max(0, targetCo - currentCo);
      if (ceGap > coGap) {
        p2 = t("coach.p2.readingGap", "Aprofundando nas seções objetivas, a Compréhension Écrite requer atenção prioritária. Concentre seus estudos em estratégias de leitura dinâmica, identificação de conectores lógicos e eliminação sistemática de distratores, buscando fechar a lacuna de {{ceGap}} nível(is) NCLC restante(s).", { ceGap });
      } else {
        p2 = t("coach.p2.listeningGap", "Aprofundando nas seções objetivas, a Compréhension Orale é nossa meta estratégica imediata. Pratique a escuta ativa de diálogos complexos e associação rápida de imagens, focando em reduzir a distância de {{coGap}} nível(is) NCLC até o alvo.", { coGap });
      }
    }

    // Parágrafo 3: Metas Qualitativas (EE e Erros)
    let p3 = t("coach.p3.intro", "No campo da Expression Écrite (EE), seu histórico conta com {{totalCorrections}} produções avaliadas. ", { totalCorrections });
    if (writingErrors.length > 0) {
      const topCat = [...errorsByCategory].sort((a, b) => b.count - a.count)[0];
      p3 += t("coach.p3.errorsFound", "Nossa inteligência pedagógica registrou um total de {{errorsCount}} desvios no Banco de Erros, com destaque crítico para desvios de \"{{topCatName}}\" ({{topCatCount}} ocorrências). Em provas do TEF, a consistência gramatical e a ortografia são rigidamente pontuadas. Dedicar 10 minutos diários para reescrever as frases corretas desse grupo acelerará sua correção de escrita de forma impressionante.", { errorsCount: writingErrors.length, topCatName: topCat.name, topCatCount: topCat.count });
    } else {
      p3 += t("coach.p3.noErrors", "Seu Banco de Erros está totalmente limpo ou intocado, o que é excelente. Continue escrevendo redações para que possamos mapear possíveis padrões de erro e refinar ainda mais a precisão da sua escrita formal.");
    }

    // Parágrafo 4: Plano Prático de Ação
    let p4 = t("coach.p4.intro", "Para a sessão de estudos de hoje, recomendo fortemente focar na execução da sua prioridade número um. ");
    if (pendingFlashcardsCount > 0) {
      p4 += t("coach.p4.flashcardsPending", "Além disso, realize a revisão das suas {{pendingFlashcardsCount}} cartas pendentes no SRS para fixar o vocabulário retido na memória de longo prazo. Clique no botão abaixo para seguir as tarefas organizadas no seu plano de estudo diário e continue acelerando em direção ao Canadá!", { pendingFlashcardsCount });
    } else {
      p4 += t("coach.p4.flashcardsDone", "Seu deck de flashcards está em dia, o que garante a fixação do seu vocabulário. Dedique o restante do seu tempo para realizar um mini-simulado ou revisar a teoria teórica da sua competência mais fraca. Clique no botão abaixo para acessar suas tarefas estruturadas no plano diário e bons estudos!");
    }

    return `${p1}\n\n${p2}\n\n${p3}\n\n${p4}`;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center space-y-4 animate-pulse">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
        <h3 className="text-base font-extrabold text-gray-800">{t("dashboard.loadingStats")}</h3>
        <p className="text-xs text-gray-400 max-w-sm leading-normal">
          {t("dashboard.fetchingHistory")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      {/* Universal focus outlines reset */}
      <style>{`
        .recharts-wrapper .recharts-surface:focus,
        .recharts-wrapper:focus,
        .recharts-wrapper *:focus {
          outline: none !important;
        }
      `}</style>

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{t("dashboard.title", "Dashboard")}</h1>
        <p className="mt-2 text-sm text-gray-500">
          {t("dashboard.subtitle", "Acompanhe seu progresso e estatísticas de estudo.")}
        </p>
      </div>

      {/* PLAN DETAILS BADGE ROW - Only for Trial/Free users */}
      {(profile && profile.plan !== "premium" && profile.role !== "admin") && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-slate-50 border border-slate-200/60 rounded-2xl p-4.5 text-left">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500">{t("dashboard.currentPlan", "Plano Atual")}:</span>
                <Badge className="font-extrabold text-[9px] uppercase tracking-wider px-2 py-0.5 border-none bg-indigo-600 text-white animate-pulse">
                  {profile.plan === "trial" ? "Trial" : "Gratuito"}
                </Badge>
              </div>
              <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider">
                {t("dashboard.usages", "Usos")}: {t("dashboard.tests", "Simulados")}: <span className="font-mono font-black text-slate-700">{profile.trial_tests_used}/4</span> · {t("dashboard.corrections", "Correções")}: <span className="font-mono font-black text-slate-700">{totalCorrections}/3</span>
              </p>
            </div>
          </div>
          <Button
            size="sm"
            asChild
            className="w-full sm:w-auto rounded-xl font-bold text-xs bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-md shadow-indigo-600/10 border-none"
          >
            <Link to="/premium">
              <span>{t("dashboard.upgradeToPremium", "Upgrade para Premium")}</span>
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Link>
          </Button>
        </div>
      )}

      {/* PERSONAL GOALS BAR */}
      {personalGoals && (() => {
        const bestCe = getBestSkillResult(historyItems, "ce")?.nclc ?? 0;
        const bestCo = getBestSkillResult(historyItems, "co")?.nclc ?? 0;

        const pCe = Math.min(100, Math.round((bestCe / (personalGoals.targetCeNclc || 7)) * 100));
        const pCo = Math.min(100, Math.round((bestCo / (personalGoals.targetCoNclc || 7)) * 100));

        const statCe = getSkillStatus(bestCe, personalGoals.targetCeNclc);
        const statCo = getSkillStatus(bestCo, personalGoals.targetCoNclc);

        const renderBadge = (st: string) => {
          if (st === "nodata") return <Badge variant="outline" className="text-[9px] font-bold text-gray-400 border-gray-200">{t("dashboard.noData", "Sem dados")}</Badge>;
          if (st === "achieved") return <Badge className="text-[9px] font-bold bg-green-100 text-green-700 border-none">{t("dashboard.goalAchieved", "Meta atingida")}</Badge>;
          return <Badge className="text-[9px] font-bold bg-amber-100 text-amber-700 border-none">{t("dashboard.inProgress", "Em progresso")}</Badge>;
        };

        return (
          <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden">
            <div className="flex flex-col gap-4 border-b border-primary/10 bg-primary/5 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
                  <Award className="w-5 h-5 text-primary shrink-0" />
                  <span>{t("dashboard.myPersonalGoals", "Minhas Metas Pessoais")}</span>
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  {t("dashboard.goalsDescription", "Objetivos definidos para seu exame TEF Canada")}
                </p>
              </div>
              <Button 
                size="sm" 
                onClick={() => {
                  setEditingGoals({
                    targetCeNclc: personalGoals.targetCeNclc ?? 7,
                    targetCoNclc: personalGoals.targetCoNclc ?? 7,
                    targetEeNclc: personalGoals.targetEeNclc ?? 7,
                    targetEoNclc: personalGoals.targetEoNclc ?? 7,
                    weeklyStudyHours: personalGoals.weeklyStudyHours ?? 7,
                    examDate: personalGoals.examDate || ""
                  });
                  setIsGoalModalOpen(true);
                }}
                className="w-full sm:w-auto rounded-xl font-bold text-xs shadow-xs mt-0 sm:mt-0"
              >
                <PenTool className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                <span>{t("dashboard.editGoals", "Editar Metas")}</span>
              </Button>
            </div>
            <CardContent className="p-6 bg-white space-y-6">
              {/* Target Goals Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col bg-purple-50/50 p-3.5 rounded-xl border border-purple-100/50 text-center">
                  <span className="text-[10px] text-purple-600 font-extrabold uppercase tracking-wider mb-1">{t("dashboard.targetCE")}</span>
                  <span className="text-2xl font-black text-purple-950 font-mono">NCLC {personalGoals.targetCeNclc}</span>
                </div>
                <div className="flex flex-col bg-blue-50/50 p-3.5 rounded-xl border border-blue-100/50 text-center">
                  <span className="text-[10px] text-blue-600 font-extrabold uppercase tracking-wider mb-1">{t("dashboard.targetCO")}</span>
                  <span className="text-2xl font-black text-blue-950 font-mono">NCLC {personalGoals.targetCoNclc}</span>
                </div>
                <div className="flex flex-col bg-gray-50 p-3.5 rounded-xl border border-gray-100 text-center">
                  <span className="text-[10px] text-gray-500 font-extrabold uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3 text-gray-400" /> {t("dashboard.studyTempo")}
                  </span>
                  <span className="text-lg font-bold text-gray-900">{personalGoals.weeklyStudyHours}h / {t("dashboard.weeklyGoalUnit")}</span>
                </div>
                <div className="flex flex-col bg-gray-50 p-3.5 rounded-xl border border-gray-100 text-center">
                  <span className="text-[10px] text-gray-500 font-extrabold uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                    <Calendar className="w-3 h-3 text-gray-400" /> {t("dashboard.examLabel")}
                  </span>
                  <span className="text-xs font-bold text-gray-900 flex items-center justify-center h-full">
                    {personalGoals.examDate ? new Date(personalGoals.examDate + "T00:00:00").toLocaleDateString(i18n.language) : t("dashboard.undefinedDate", "Not set")}
                  </span>
                </div>
              </div>

              {/* Progress to Goal Row */}
              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  {t("dashboard.progressToGoal", "Progresso até a Meta")}
                </h3>
                
                <div className="grid gap-4 md:grid-cols-2">
                  <Card className="border border-purple-100 bg-purple-50/10 hover:bg-white hover:shadow-xs transition-all duration-300 rounded-2xl p-5 flex flex-col justify-between text-left shadow-none">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-xs font-extrabold text-purple-600 block">Compréhension Écrite (CE)</span>
                        <span className="text-[10px] text-gray-400 font-bold block">{calculateProgressToGoal(bestCe, personalGoals.targetCeNclc, t)}</span>
                      </div>
                      {renderBadge(statCe)}
                    </div>
                    <div>
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-2xl font-black text-purple-900 font-mono">NCLC {bestCe}</span>
                        <span className="text-xs font-bold text-gray-400 font-mono">{t("dashboard.goalLabel", "Meta:")} {personalGoals.targetCeNclc}</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-purple-600 h-full rounded-full transition-all duration-500" style={{ width: `${pCe}%` }} />
                      </div>
                    </div>
                  </Card>

                  <Card className="border border-blue-100 bg-blue-50/10 hover:bg-white hover:shadow-xs transition-all duration-300 rounded-2xl p-5 flex flex-col justify-between text-left shadow-none">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-xs font-extrabold text-blue-600 block">Compréhension Orale (CO)</span>
                        <span className="text-[10px] text-gray-400 font-bold block">{calculateProgressToGoal(bestCo, personalGoals.targetCoNclc, t)}</span>
                      </div>
                      {renderBadge(statCo)}
                    </div>
                    <div>
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-2xl font-black text-blue-950 font-mono">NCLC {bestCo}</span>
                        <span className="text-xs font-bold text-gray-400 font-mono">{t("dashboard.goalLabel", "Meta:")} {personalGoals.targetCoNclc}</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                        <div className="bg-blue-600 h-full rounded-full transition-all duration-500" style={{ width: `${pCo}%` }} />
                      </div>
                    </div>
                  </Card>
                </div>
              </div>

              {/* Separator & Title */}
              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  {t("dashboard.myProgressStats", "Meu Progresso e Estatísticas")}
                </h3>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {/* 1. CE Average Card */}
                  <Card className="border border-purple-100 bg-purple-50/10 hover:bg-white hover:shadow-xs transition-all duration-300 rounded-2xl flex flex-col justify-between min-h-[220px] h-auto text-left p-0">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6 px-6">
                      <CardTitle className="text-sm font-semibold text-gray-500">{t("dashboard.averageCE", "Média Compréhension Écrite (CE)")}</CardTitle>
                      <BookOpen className="w-4 h-4 text-purple-500" />
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-between pb-6 px-6 pt-2">
                      <div>
                        {hasReadingData && readingLevelInfo ? (
                          <div className="space-y-2">
                            <div className="flex items-end gap-1.5 leading-none">
                              <span className="text-4xl md:text-5xl font-extrabold text-purple-600 tracking-tight">{readingAverage}</span>
                              <span className="pb-1 text-base font-medium text-slate-400">/300</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="inline-flex rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-semibold text-purple-700">
                                {readingLevelInfo.label}
                              </div>
                              <p className="text-[11px] text-slate-500 font-medium">{t("dashboard.rangeCE", "Faixa CE")}: {readingLevelInfo.range}</p>
                            </div>
                            <p className="text-[10px] text-green-500 font-semibold uppercase tracking-wider">{t("dashboard.realPerformanceHistory", "Desempenho real do histórico")}</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-end gap-1.5 leading-none">
                              <span className="text-4xl md:text-5xl font-extrabold text-purple-600 tracking-tight">0</span>
                              <span className="pb-1 text-base font-medium text-slate-400">/300</span>
                            </div>
                            <p className="text-xs text-slate-400 italic">{t("dashboard.waitingFirstTest", "Aguardando primeiro teste")}</p>
                          </div>
                        )}
                      </div>

                      <div className="mt-4">
                        {hasReadingData && readingLevelInfo ? (
                          readingGap.achieved ? (
                            <div className="rounded-xl bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                              {userGoals.isCustom ? t("dashboard.customGoal", "Meta") : t("dashboard.defaultGoal", "Meta padrão")} {t("dashboard.achieved", "atingida")} · NCLC {readingNclcGoal} {t("dashboard.reached", "alcançado")}
                            </div>
                          ) : (
                            <div className="rounded-xl bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">
                              {userGoals.isCustom ? t("dashboard.customGoal", "Meta") : t("dashboard.defaultGoal", "Meta padrão")}: NCLC {readingNclcGoal} · {t("dashboard.missingPoints", "Faltam {{count}} pontos", { count: readingGap.missingPoints })}
                            </div>
                          )
                        ) : (
                          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
                            {userGoals.isCustom ? t("dashboard.customGoal", "Meta") : t("dashboard.defaultGoal", "Meta padrão")}: NCLC {readingNclcGoal} · {t("dashboard.targetCE", "Alvo CE")}: {readingGap.targetScore}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* 2. CO Average Card */}
                  <Card className="border border-blue-100 bg-blue-50/10 hover:bg-white hover:shadow-xs transition-all duration-300 rounded-2xl flex flex-col justify-between min-h-[220px] h-auto text-left p-0">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6 px-6">
                      <CardTitle className="text-sm font-semibold text-gray-500">{t("dashboard.averageCO", "Média Compréhension Orale (CO)")}</CardTitle>
                      <Headphones className="w-4 h-4 text-blue-500" />
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-between pb-6 px-6 pt-2">
                      <div>
                        {hasListeningData && listeningLevelInfo ? (
                          <div className="space-y-2">
                            <div className="flex items-end gap-1.5 leading-none">
                              <span className="text-4xl md:text-5xl font-extrabold text-blue-600 tracking-tight">{listeningAverage}</span>
                              <span className="pb-1 text-base font-medium text-slate-400">/360</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="inline-flex rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                                {listeningLevelInfo.label}
                              </div>
                              <p className="text-[11px] text-slate-500 font-medium">{t("dashboard.rangeCO", "Faixa CO")}: {listeningLevelInfo.range}</p>
                            </div>
                            <p className="text-[10px] text-green-500 font-semibold uppercase tracking-wider">{t("dashboard.realPerformanceHistory", "Desempenho real do histórico")}</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-end gap-1.5 leading-none">
                              <span className="text-4xl md:text-5xl font-extrabold text-blue-600 tracking-tight">0</span>
                              <span className="pb-1 text-base font-medium text-slate-400">/360</span>
                            </div>
                            <p className="text-xs text-slate-400 italic">{t("dashboard.waitingFirstTest", "Aguardando primeiro teste")}</p>
                          </div>
                        )}
                      </div>

                      <div className="mt-4">
                        {hasListeningData && listeningLevelInfo ? (
                          listeningGap.achieved ? (
                            <div className="rounded-xl bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                              {userGoals.isCustom ? t("dashboard.customGoal", "Meta") : t("dashboard.defaultGoal", "Meta padrão")} {t("dashboard.achieved", "atingida")} · NCLC {listeningNclcGoal} {t("dashboard.reached", "alcançado")}
                            </div>
                          ) : (
                            <div className="rounded-xl bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">
                              {userGoals.isCustom ? t("dashboard.customGoal", "Meta") : t("dashboard.defaultGoal", "Meta padrão")}: NCLC {listeningNclcGoal} · {t("dashboard.missingPoints", "Faltam {{count}} pontos", { count: listeningGap.missingPoints })}
                            </div>
                          )
                        ) : (
                          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
                            {userGoals.isCustom ? t("dashboard.customGoal", "Meta") : t("dashboard.defaultGoal", "Meta padrão")}: NCLC {listeningNclcGoal} · {t("dashboard.targetCO", "Alvo CO")}: {listeningGap.targetScore}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* 3. Writing (EE) Card */}
                  <Card className="border border-emerald-100/40 bg-gradient-to-br from-emerald-50/20 to-teal-50/10 hover:from-emerald-50/40 hover:to-teal-50/30 hover:shadow-xs transition-all duration-300 rounded-2xl flex flex-col justify-between min-h-[220px] h-auto text-left p-0">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6 px-6">
                      <CardTitle className="text-sm font-bold text-emerald-800">{t("dashboard.writingTitle", "Expression Écrite")}</CardTitle>
                      <PenTool className="w-4 h-4 text-emerald-600" />
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-between pb-6 px-6 pt-2">
                      <div>
                        <div className="space-y-2">
                          <div className="flex items-end gap-1.5 leading-none">
                            <span className="text-4xl md:text-5xl font-black text-emerald-700 tracking-tight">
                              {totalSubmitted}
                            </span>
                            <span className="pb-1 text-sm font-semibold text-emerald-600">
                              {totalSubmitted === 1 ? t("dashboard.productionSubmitted", "produção entregue") : t("dashboard.productionsSubmitted", "produções entregues")}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="inline-flex rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 text-[10px] font-extrabold text-emerald-700 uppercase tracking-wider">
                              {t("dashboard.activeCorrection", "Correção Ativa")}
                            </div>
                            <p className="text-[10px] text-slate-500 font-bold">{t("dashboard.pedagogicalFeedback", "Feedback Pedagógico")}</p>
                          </div>
                          <p className="text-[11px] text-slate-600 leading-relaxed font-medium mt-1">
                            {t("dashboard.writingProgressDesc", "Seu progresso na Expression Écrite é puramente pedagógico e qualitativo, focado na eliminação sistemática de desvios gramaticais.")}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-3 py-2 text-xs font-bold text-emerald-800 flex items-center justify-between">
                          <span>{t("dashboard.activeErrorBank", "Banco de Erros ativo")}</span>
                          <span className="text-[10px] font-extrabold bg-emerald-600 text-white px-2 py-0.5 rounded-full">{t("dashboard.errorsCount", "{{count}} erros", { count: writingErrors.length })}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 4. Flashcards Card */}
                  <Card className="border border-amber-100/60 bg-amber-50/10 hover:bg-white hover:shadow-xs transition-all duration-300 rounded-2xl flex flex-col justify-between min-h-[220px] h-auto p-0 text-left">
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6 px-6">
                          <CardTitle className="text-sm font-semibold text-gray-500">{t("dashboard.flashcardsPending", "Flashcards Pendentes")}</CardTitle>
                          <Layers className="w-4 h-4 text-amber-500" />
                        </CardHeader>
                        <CardContent className="pb-0 px-6 pt-2">
                          <div className="text-2xl font-bold text-gray-900">{pendingFlashcardsCount}</div>
                          <p className="text-xs text-gray-500 mt-1">{t("dashboard.toReviewToday", "Para revisar hoje")}</p>
                        </CardContent>
                      </div>
                      {pendingFlashcardsCount > 0 && (
                        <div className="px-6 pb-6 mt-4">
                          <Link to="/flashcards/study">
                            <Button className="w-full h-8 text-xs font-bold rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-700 border-none shadow-none">
                              {t("dashboard.studyNow", "Estudar Agora")}
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* 5. Simulados Card */}
                  <Card className="border border-slate-100 bg-slate-50/30 hover:bg-white hover:shadow-xs transition-all duration-300 rounded-2xl flex flex-col justify-between min-h-[220px] h-auto p-0 text-left">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6 px-6">
                      <CardTitle className="text-sm font-semibold text-gray-500">{t("dashboard.simulatesDone", "Simulados Realizados")}</CardTitle>
                      <Activity className="w-4 h-4 text-blue-500" />
                    </CardHeader>
                    <CardContent className="pb-6 px-6 pt-2">
                      <div className="text-2xl font-bold text-gray-900">{simulationsCompletedCount}</div>
                      <p className="text-xs text-gray-500 mt-1">{t("dashboard.inCurrentMonth", "Neste mês corrente")}</p>
                    </CardContent>
                  </Card>

                  {/* 6. Streak (Sequência) Card */}
                  <Card className="border border-slate-100 bg-slate-50/30 hover:bg-white hover:shadow-xs transition-all duration-300 rounded-2xl flex flex-col justify-between min-h-[220px] h-auto p-0 text-left">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6 px-6">
                      <CardTitle className="text-sm font-semibold text-gray-500">{t("dashboard.streak", "Sequência")}</CardTitle>
                      <Flame className="w-4 h-4 text-orange-500" />
                    </CardHeader>
                    <CardContent className="pb-6 px-6 pt-2">
                      <div className="text-2xl font-bold text-gray-900">{streakDays} {streakDays === 1 ? t("dashboard.day", "dia") : t("dashboard.days", "dias")}</div>
                      <p className="text-xs text-gray-500 mt-1">{t("dashboard.consecutiveStudySessions", "Sessões de estudo consecutivas")}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* GETTING STARTED CHECKLIST CARD */}
      {!isChecklistDismissed && personalGoals && (
        <Card className={`border-none shadow-sm transition-all duration-300 rounded-2xl overflow-hidden mt-6 ${
          isChecklistAllCompleted 
            ? "bg-gradient-to-r from-emerald-50/60 to-teal-50/60 border border-emerald-100" 
            : "bg-white"
        }`}>
          <div className="flex items-center justify-between border-b border-primary/10 bg-primary/5 px-6 py-4">
            <div className="flex items-center gap-2">
              <ClipboardCheck className={`w-5 h-5 ${isChecklistAllCompleted ? "text-emerald-600 animate-bounce" : "text-primary"}`} />
              <div>
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <span>{t("gettingStarted.title", "Getting Started Checklist")}</span>
                  <Badge variant="outline" className={`text-[10px] font-bold ${
                    isChecklistAllCompleted 
                      ? "bg-emerald-100 border-none text-emerald-800" 
                      : "bg-primary/10 border-none text-primary"
                  }`}>
                    {checklistCompletedCount}/5
                  </Badge>
                </h3>
                {!isChecklistCollapsed && (
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {t("gettingStarted.subtitle", "Complete these initial milestones to master your preparation")}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  const newVal = !isChecklistCollapsed;
                  setIsChecklistCollapsed(newVal);
                  localStorage.setItem("tef_getting_started_collapsed", String(newVal));
                  
                  if (personalGoals) {
                    const updatedMeta = {
                      ...personalGoals.metadata,
                      gettingStartedChecklist: {
                        ...personalGoals.metadata?.gettingStartedChecklist,
                        collapsed: newVal
                      }
                    };
                    const res = await userGoalService.upsertUserGoals({ metadata: updatedMeta });
                    setPersonalGoals(res);
                  }
                }}
                className="h-8 w-8 p-0 hover:bg-slate-200/50 text-slate-500 rounded-lg"
              >
                {isChecklistCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  setIsChecklistDismissed(true);
                  localStorage.setItem("tef_getting_started_dismissed", "true");
                  
                  if (personalGoals) {
                    const updatedMeta = {
                      ...personalGoals.metadata,
                      gettingStartedChecklist: {
                        ...personalGoals.metadata?.gettingStartedChecklist,
                        dismissed: true
                      }
                    };
                    const res = await userGoalService.upsertUserGoals({ metadata: updatedMeta });
                    setPersonalGoals(res);
                  }
                }}
                className="h-8 w-8 p-0 hover:bg-slate-200/50 text-slate-500 rounded-lg"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {!isChecklistCollapsed && (
            <CardContent className="p-6 bg-white/40 backdrop-blur-xs">
              {isChecklistAllCompleted && (
                <div className="mb-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center animate-in fade-in zoom-in-95 duration-300">
                  <span className="text-xs font-black text-emerald-800 flex items-center justify-center gap-1.5">
                    🎉 {t("gettingStarted.congrats", "Congratulations! All initial steps are completed!")}
                  </span>
                  <p className="text-[10px] text-emerald-600 mt-0.5 font-semibold">
                    {t("gettingStarted.congratsSub", "You are fully set to succeed in your TEF Canada preparation.")}
                  </p>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                {checklistItems.map((item) => (
                  <div 
                    key={item.id}
                    className={`p-3.5 rounded-xl border transition-all flex items-center gap-3 ${
                      item.checked 
                        ? "bg-emerald-50/40 border-emerald-100/70 text-emerald-900" 
                        : "bg-slate-50/50 border-slate-100 hover:border-slate-200 text-slate-700"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border transition-all ${
                      item.checked 
                        ? "bg-emerald-500 border-emerald-500 text-white" 
                        : "border-slate-300 bg-white"
                    }`}>
                      {item.checked && <Check className="w-3 h-3 stroke-[3]" />}
                    </div>
                    <span className={`text-[11px] font-bold leading-snug ${item.checked ? "line-through text-slate-400 font-medium" : ""}`}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* STUDY TIME TRACKER CARD */}
      {personalGoals && (() => {
        const goalSeconds = (personalGoals.weeklyStudyHours || 7) * 3600;
        const progressPercent = Math.min(100, Math.round((studyTimeWeekly / goalSeconds) * 100));
        const isGoalReached = studyTimeWeekly >= goalSeconds;
        const timeLeftSeconds = Math.max(0, goalSeconds - studyTimeWeekly);

        const motivationalMessage = isGoalReached
          ? t("studyTime.goalReached", "Great job — you reached your weekly study goal.")
          : t("studyTime.timeLeft", "Keep going — you still have {{time}} left this week.").replace("{{time}}", formatSecondsToHoursMinutes(timeLeftSeconds, t));

        const activitiesList = [
          { label: t("studyTime.reading", "Compreensão Escrita"), value: studyTimeByCompetency.reading, color: "bg-purple-500" },
          { label: t("studyTime.listening", "Compreensão Oral"), value: studyTimeByCompetency.listening, color: "bg-blue-500" },
          { label: t("studyTime.writing", "Expressão Escrita"), value: studyTimeByCompetency.writing, color: "bg-emerald-500" },
          { label: t("studyTime.flashcards", "Flashcards"), value: studyTimeByCompetency.flashcards, color: "bg-amber-500" },
          { label: t("studyTime.errorBank", "Banco de Erros"), value: studyTimeByCompetency.errorBank, color: "bg-rose-500" }
        ];

        return (
          <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden mt-6">
            <div className="flex flex-col gap-4 border-b border-indigo-100 bg-indigo-50/30 px-6 py-5 sm:flex-row sm:items-center sm:justify-between text-left">
              <div>
                <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
                  <Clock className="w-5 h-5 text-indigo-600 shrink-0" />
                  <span>{t("studyTime.title", "Tempo de Estudo")}</span>
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  {t("studyTime.subtitle", "Acompanhe suas horas de estudo ativo e progresso semanal.")}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingGoals({
                    targetCeNclc: personalGoals.targetCeNclc ?? 7,
                    targetCoNclc: personalGoals.targetCoNclc ?? 7,
                    targetEeNclc: personalGoals.targetEeNclc ?? 7,
                    targetEoNclc: personalGoals.targetEoNclc ?? 7,
                    weeklyStudyHours: personalGoals.weeklyStudyHours ?? 7,
                    examDate: personalGoals.examDate || ""
                  });
                  setIsGoalModalOpen(true);
                }}
                className="w-full sm:w-auto rounded-xl font-bold text-xs border-indigo-100 text-indigo-700 hover:bg-indigo-50"
              >
                <span>{t("studyTime.editWeeklyGoal", "Editar meta semanal")}</span>
              </Button>
            </div>
            
            <CardContent className="p-6 bg-white space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {/* Metric 1: Today */}
                <div className="flex flex-col bg-slate-50/50 p-4 rounded-2xl border border-slate-100/50 text-left space-y-1">
                  <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">{t("studyTime.today", "Hoje")}</span>
                  <span className="text-2xl font-black text-slate-800">{formatSecondsToHoursMinutes(studyTimeToday, t)}</span>
                </div>
                
                {/* Metric 2: This Week */}
                <div className="flex flex-col bg-slate-50/50 p-4 rounded-2xl border border-slate-100/50 text-left space-y-1">
                  <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">{t("studyTime.thisWeek", "Esta semana")}</span>
                  <span className="text-2xl font-black text-slate-800">{formatSecondsToHoursMinutes(studyTimeWeekly, t)}</span>
                </div>
                
                {/* Metric 3: Weekly Goal */}
                <div className="flex flex-col bg-slate-50/50 p-4 rounded-2xl border border-slate-100/50 text-left space-y-1">
                  <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">{t("studyTime.weeklyGoal", "Meta semanal")}</span>
                  <span className="text-2xl font-black text-slate-800">{personalGoals.weeklyStudyHours}h</span>
                </div>
              </div>

              {/* Progress Bar & Message */}
              <div className="space-y-2.5 text-left bg-indigo-50/10 border border-indigo-100/30 p-5 rounded-2xl">
                <div className="flex justify-between items-center text-xs font-bold">
                  <span className="text-indigo-950">{motivationalMessage}</span>
                  <span className="font-mono text-indigo-600">{progressPercent}%</span>
                </div>
                <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                  <div 
                    className="bg-indigo-600 h-full rounded-full transition-all duration-500" 
                    style={{ width: `${progressPercent}%` }} 
                  />
                </div>
              </div>

              {/* Competency distribution */}
              <div className="space-y-4 text-left">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  {t("studyTime.thisWeekByActivity", "Esta semana por atividade")}
                </h3>
                <div className="space-y-3 bg-slate-50/50 p-5 rounded-2xl border border-slate-100/50">
                  {activitiesList.map((act, idx) => {
                    const percent = studyTimeWeekly > 0 ? Math.round((act.value / studyTimeWeekly) * 100) : 0;
                    return (
                      <div key={idx} className="space-y-1.5">
                        <div className="flex justify-between text-xs font-semibold text-slate-700">
                          <span className="font-bold">{act.label}</span>
                          <span className="font-mono text-slate-500">
                            {formatSecondsToHoursMinutes(act.value, t)} ({percent}%)
                          </span>
                        </div>
                        <div className="w-full bg-slate-200/50 h-2 rounded-full overflow-hidden">
                          <div 
                            className={`${act.color} h-full rounded-full transition-all duration-300`} 
                            style={{ width: `${percent}%` }} 
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* CURRENT STATUS PREMIUM SECTION */}
      {false && personalGoals && (() => {

        const parseNclcToNumber = (nclcStr: string | number | undefined): number => {
          if (nclcStr === undefined || nclcStr === null) return 0;
          if (typeof nclcStr === "number") return nclcStr;
          const str = String(nclcStr).trim();
          if (str.includes("<")) return 2;
          if (str.includes("+")) return 10;
          const parsed = parseInt(str);
          return isNaN(parsed) ? 0 : parsed;
        };

        const targetCeNclc = personalGoals.targetCeNclc ?? parseNclcToNumber(readingNclcGoal) ?? 7;
        const targetCoNclc = personalGoals.targetCoNclc ?? parseNclcToNumber(listeningNclcGoal) ?? 7;
        const targetEeNclc = personalGoals.targetEeNclc ?? parseNclcToNumber(writingNclcGoal) ?? 7;

        const currentCeNclc = readingLevelInfo ? parseNclcToNumber(readingLevelInfo.nclc) : 0;
        const currentCoNclc = listeningLevelInfo ? parseNclcToNumber(listeningLevelInfo.nclc) : 0;

        const ceGap = Math.max(0, targetCeNclc - currentCeNclc);
        const coGap = Math.max(0, targetCoNclc - currentCoNclc);

        const getStatusBadge = (gap: number, hasData: boolean) => {
          if (!hasData) return <Badge className="text-[10px] font-bold bg-slate-100 text-slate-400 border-none">{t("performanceBySection.notEnoughData", "Données insuffisantes")}</Badge>;
          if (gap >= 3) return <Badge className="text-[10px] font-bold bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-50">{t("performanceBySection.critical", "Critique")}</Badge>;
          if (gap === 2) return <Badge className="text-[10px] font-bold bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-50">{t("performanceBySection.needsAttention", "À travailler")}</Badge>;
          if (gap === 1) return <Badge className="text-[10px] font-bold bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-50">{t("performanceBySection.onTrack", "En bonne voie")}</Badge>;
          return <Badge className="text-[10px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-50">{t("dashboard.goalAchievedBadge", "Objectif atteint")}</Badge>;
        };

        const hasWritingData = totalCorrections > 0 || writingErrors.length > 0;

        // Auto Next Recommended Action
        let recommendedActionTitle = "";
        let recommendedActionDesc = "";
        let recommendedActionPath = "";
        let recommendedActionLabel = "";

        if (hasReadingData && currentCeNclc < targetCeNclc) {
          const gap = targetCeNclc - currentCeNclc;
          recommendedActionTitle = t("dashboard.recommendations.readingScoreBelowTargetTitle", { defaultValue: "Your Reading score is {{gap}} NCLC level(s) below your target.", gap });
          recommendedActionDesc = t("dashboard.recommendations.readingScoreBelowTargetDesc", "Take one Reading test and review your recurring errors.");
          recommendedActionPath = "/start-test?competence=reading";
          recommendedActionLabel = t("dashboard.startAction", "Start Now");
        } else if (hasListeningData && currentCoNclc < targetCoNclc) {
          const gap = targetCoNclc - currentCoNclc;
          recommendedActionTitle = t("dashboard.recommendations.listeningScoreBelowTargetTitle", { defaultValue: "Your Listening score is {{gap}} NCLC level(s) below your target.", gap });
          recommendedActionDesc = t("dashboard.recommendations.listeningScoreBelowTargetDesc", "Take one Listening test and review your recurring errors.");
          recommendedActionPath = "/start-test?competence=listening";
          recommendedActionLabel = t("dashboard.startAction", "Start Now");
        } else if (pendingFlashcardsCount > 0) {
          recommendedActionTitle = t("dashboard.recommendations.flashcardsDueTodayTitle", { defaultValue: "You have {{count}} flashcards due for review today.", count: pendingFlashcardsCount });
          recommendedActionDesc = t("dashboard.recommendations.flashcardsDueTodayDesc", "Practice spaced repetition (SRS) to reinforce your memory.");
          recommendedActionPath = "/flashcards";
          recommendedActionLabel = t("dashboard.startAction", "Start Now");
        } else if (writingErrors.length > 0) {
          recommendedActionTitle = t("dashboard.recommendations.activeErrorsTitle", { defaultValue: "You have {{count}} active errors waiting in your Error Bank.", count: writingErrors.length });
          recommendedActionDesc = t("dashboard.recommendations.activeErrorsDesc", "Review your grammar, preposition, and spelling desvios to avoid repeating them.");
          recommendedActionPath = "/writing-errors";
          recommendedActionLabel = t("dashboard.startAction", "Start Now");
        } else {
          recommendedActionTitle = t("dashboard.recommendations.writingHistoryTitle", "Continue building your writing history.");
          recommendedActionDesc = t("dashboard.recommendations.writingHistoryDesc", "Submit another essay for AI pedagogical correction to expand your statistics.");
          recommendedActionPath = "/start-test?competence=writing";
          recommendedActionLabel = t("dashboard.startAction", "Start Now");
        }

        return (
          <div className="space-y-4">
            {/* Card Principal Current Status */}
            <Card className="border-none shadow-sm bg-white rounded-2xl p-6 text-left">
              <div className="border-b border-slate-100 pb-4 mb-5">
                <h3 className="text-lg font-black text-slate-800 tracking-tight">{t("dashboard.currentPlan", "Plano Atual")}</h3>
                <p className="text-slate-500 text-xs font-semibold mt-0.5">{t("dashboard.currentPositionCompared", "Sua posição atual em relação às suas metas de estudo.")}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
                {/* COLUMN 1: Reading (CE) */}
                <div className="space-y-3 pt-4 md:pt-0">
                  <div className="flex items-center justify-between">
                    <h4 className="font-extrabold text-sm text-slate-700">{t("performanceBySection.tabs.reading", "Compréhension écrite")} (CE)</h4>
                    {getStatusBadge(ceGap, hasReadingData)}
                  </div>
                  {hasReadingData ? (
                    <div className="space-y-1 mt-2">
                      <div className="text-sm font-bold text-slate-800">
                        {t("dashboard.actualLabel", "Atual:")} <span className="text-slate-900 font-extrabold font-mono">NCLC {currentCeNclc}</span>
                      </div>
                      <div className="text-xs font-semibold text-slate-500">
                        {t("dashboard.goalLabel", "Meta:")} <span className="font-bold font-mono">NCLC {targetCeNclc}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-400 italic text-xs mt-2 font-semibold">{t("performanceBySection.notEnoughData", "Données insuffisantes")}</div>
                  )}
                </div>

                {/* COLUMN 2: Listening (CO) */}
                <div className="space-y-3 pt-4 md:pt-0 md:pl-6">
                  <div className="flex items-center justify-between">
                    <h4 className="font-extrabold text-sm text-slate-700">{t("performanceBySection.tabs.listening", "Compréhension orale")} (CO)</h4>
                    {getStatusBadge(coGap, hasListeningData)}
                  </div>
                  {hasListeningData ? (
                    <div className="space-y-1 mt-2">
                      <div className="text-sm font-bold text-slate-800">
                        {t("dashboard.actualLabel", "Atual:")} <span className="text-slate-900 font-extrabold font-mono">NCLC {currentCoNclc}</span>
                      </div>
                      <div className="text-xs font-semibold text-slate-500">
                        {t("dashboard.goalLabel", "Meta:")} <span className="font-bold font-mono">NCLC {targetCoNclc}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-400 italic text-xs mt-2 font-semibold">{t("performanceBySection.notEnoughData", "Données insuffisantes")}</div>
                  )}
                </div>

                {/* COLUMN 3: Writing (EE) */}
                <div className="space-y-3 pt-4 md:pt-0 md:pl-6">
                  <h4 className="font-extrabold text-sm text-slate-700">{t("dashboard.writingProgressTitle", "Progresso em Expression Écrite")}</h4>
                  {hasWritingData ? (
                    <div className="space-y-1.5 mt-2">
                      <div className="text-sm font-bold text-slate-800">
                        {totalCorrections} {totalCorrections === 1 ? t("dashboard.productionSubmitted", "production soumise") : t("dashboard.productionsSubmitted", "productions soumises")}
                      </div>
                      <div className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse shrink-0"></span>
                        {writingErrors.length} {writingErrors.length === 1 ? t("dashboard.activeError", "erro ativo") : t("dashboard.activeError_plural", "erros ativos")}
                      </div>
                      {topFocusCategory !== "N/A" && (
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider pt-1">
                          {t("dashboard.focus", "Focus:")} <span className="text-slate-700 capitalize font-extrabold">{translateErrorCategory(topFocusCategory, t)}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-slate-400 italic text-xs mt-2 font-semibold">{t("performanceBySection.notEnoughData", "Données insuffisantes")}</div>
                  )}
                </div>
              </div>
            </Card>

            {/* Next Recommended Action Banner */}
            <Card className="border-none shadow-sm bg-gradient-to-r from-purple-50 via-indigo-50/50 to-white rounded-2xl p-5 border border-purple-100/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-left">
              <div className="space-y-1">
                <span className="text-[10px] text-primary font-black uppercase tracking-wider block">{t("dashboard.smartRecommendationTitle", "Recommandation intelligente")}</span>
                <h4 className="font-black text-slate-800 text-sm">{recommendedActionTitle}</h4>
                <p className="text-xs text-slate-500 font-semibold">{recommendedActionDesc}</p>
              </div>
              <Button
                onClick={() => navigate(recommendedActionPath)}
                className="bg-primary hover:bg-primary/95 text-white font-extrabold text-xs px-5 h-10 rounded-xl shadow-sm shrink-0 self-start sm:self-auto"
              >
                {recommendedActionLabel}
              </Button>
            </Card>
          </div>
        );
      })()}


      {/* PERFORMANCE BY SECTION CARD BLOCK */}
      {personalGoals && (() => {
        // Filter sections based on active tab
        const filteredList = renderedSections.filter(sec => {
          if (performanceTab === "all") return true;
          if (performanceTab === "reading") return sec.competence === "Reading";
          if (performanceTab === "listening") return sec.competence === "Listening";
          if (performanceTab === "writing") return sec.competence === "Writing";
          return true;
        });

        const getStatusBadge = (st: string) => {
          if (st === "Strong") return <Badge className="text-[10px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-50">Strong</Badge>;
          if (st === "On Track") return <Badge className="text-[10px] font-bold bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-50">On Track</Badge>;
          if (st === "Needs Attention") return <Badge className="text-[10px] font-bold bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-50">Needs Attention</Badge>;
          return <Badge className="text-[10px] font-bold bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-50">Critical</Badge>;
        };

        const getTrendBadge = (tr: string) => {
          if (tr === "Improving") return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100">↑ {t("performanceBySection.improving", "Improving")}</span>;
          if (tr === "Stable") return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100">→ {t("performanceBySection.stable", "Stable")}</span>;
          if (tr === "Declining") return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-100">↓ {t("performanceBySection.declining", "Declining")}</span>;
          return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50/50 px-2 py-0.5 rounded-md border border-slate-100/50">{t("performanceBySection.notEnoughData", "Not enough data")}</span>;
        };

        const getCompetenceIcon = (comp: string) => {
          if (comp === "Reading") return <BookOpen className="w-4 h-4 text-purple-500 shrink-0" />;
          if (comp === "Listening") return <Headphones className="w-4 h-4 text-blue-500 shrink-0" />;
          return <PenTool className="w-4 h-4 text-emerald-500 shrink-0" />;
        };

        const getCompetenceLabel = (comp: string) => {
          if (comp === "Reading") return "Reading (CE)";
          if (comp === "Listening") return "Listening (CO)";
          return "Writing (EE)";
        };

        return (
          <Card className="border-none shadow-sm bg-white rounded-2xl p-6 text-left">
            {/* Header */}
            <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${isPerformanceExpanded ? "border-b border-slate-100 pb-4 mb-5" : ""}`}>
              <div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight">{t("performanceBySection.title", "Performance by Section")}</h3>
                <p className="text-slate-500 text-xs font-semibold mt-0.5">{t("performanceBySection.subtitle", "See your strengths and weaknesses across TEF sections.")}</p>
              </div>

              <div className="flex items-center gap-3 self-start sm:self-auto">
                {/* Filters Buttons */}
                {isPerformanceExpanded && (
                  <div className="flex flex-wrap gap-1.5 bg-slate-50 p-1 rounded-xl border border-slate-200/50">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setPerformanceTab("all")}
                      className={`rounded-lg text-[10px] font-black h-7 px-3 transition-all ${performanceTab === "all" ? "bg-white text-primary shadow-xs" : "text-slate-500 hover:bg-slate-100"}`}
                    >
                      {t("performanceBySection.tabs.all", "All")}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setPerformanceTab("reading")}
                      className={`rounded-lg text-[10px] font-black h-7 px-3 transition-all ${performanceTab === "reading" ? "bg-white text-primary shadow-xs" : "text-slate-500 hover:bg-slate-100"}`}
                    >
                      {t("performanceBySection.tabs.reading", "Reading")}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setPerformanceTab("listening")}
                      className={`rounded-lg text-[10px] font-black h-7 px-3 transition-all ${performanceTab === "listening" ? "bg-white text-primary shadow-xs" : "text-slate-500 hover:bg-slate-100"}`}
                    >
                      {t("performanceBySection.tabs.listening", "Listening")}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setPerformanceTab("writing")}
                      className={`rounded-lg text-[10px] font-black h-7 px-3 transition-all ${performanceTab === "writing" ? "bg-white text-primary shadow-xs" : "text-slate-500 hover:bg-slate-100"}`}
                    >
                      {t("performanceBySection.tabs.writing", "Writing")}
                    </Button>
                  </div>
                )}

                {/* Toggle Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={togglePerformance}
                  className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 shrink-0"
                  aria-label={isPerformanceExpanded ? "Collapse" : "Expand"}
                >
                  {isPerformanceExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {isPerformanceExpanded && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredList.map((sec, index) => {
                  const actionPath = sec.competence === "Reading" ? "/start-test?competence=reading" : 
                                     sec.competence === "Listening" ? "/start-test?competence=listening" : 
                                     "/start-test?competence=writing";

                  return (
                    <div 
                      key={index} 
                      className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100 flex flex-col justify-between gap-4 hover:border-primary/20 hover:bg-white transition-all duration-200"
                    >
                      <div>
                        {/* Section Info Header */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2.5 min-w-0">
                            <div className="w-8 h-8 rounded-xl bg-white border border-slate-200/50 flex items-center justify-center shrink-0 shadow-xs">
                              {getCompetenceIcon(sec.competence)}
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-extrabold text-xs text-slate-800 leading-snug truncate" title={sec.name}>
                                {sec.name}
                              </h4>
                              <span className="text-[10px] text-slate-400 font-bold">
                                {getCompetenceLabel(sec.competence)}
                              </span>
                            </div>
                          </div>
                          {sec.hasData && getStatusBadge(sec.status)}
                        </div>

                        {/* Section Telemetry Stats */}
                        {sec.hasData ? (
                          <div className="mt-4 grid grid-cols-2 gap-y-3 gap-x-2 text-[11px] font-semibold text-slate-500 border-t border-slate-100/50 pt-3">
                            {sec.competence === "Writing" ? (
                              <>
                                <div>
                                  {t("performanceBySection.avgErrors", "Avg. errors")}: <span className="font-black text-slate-800">{sec.avgErrors}</span>
                                </div>
                                <div>
                                  {t("performanceBySection.answered", "Corrected")}: <span className="font-black text-slate-800">{sec.correctionsCount}</span>
                                </div>
                                <div className="col-span-2 truncate">
                                  {t("dashboard.highestPriority", "Main issue")}: <span className="font-black text-slate-700 capitalize">{sec.mainErrorFocus}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  {t("performanceBySection.accuracy", "Accuracy")}: <span className="font-black text-slate-800">{sec.accuracy}%</span>
                                </div>
                                <div>
                                  {t("performanceBySection.answered", "Answered")}: <span className="font-black text-slate-800">{sec.total}</span>
                                </div>
                                <div>
                                  {t("performanceBySection.correct", "Correct")}: <span className="font-black text-slate-800">{sec.correct}</span>
                                </div>
                                <div>
                                  {t("performanceBySection.lastResult", "Last result")}: <span className="font-black text-slate-800">{sec.lastResult}%</span>
                                </div>
                              </>
                            )}
                            <div className="col-span-2 flex items-center gap-1.5 pt-1">
                              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">
                                {t("performanceBySection.trend", "Trend")}:
                              </span>
                              {getTrendBadge(sec.trend)}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-6 text-slate-400 italic text-[11px] font-semibold border-t border-slate-100/50 pt-3 flex items-center justify-between">
                            <span>{t("performanceBySection.notEnoughData", "Not enough data yet")}</span>
                            {getTrendBadge(sec.trend)}
                          </div>
                        )}
                      </div>

                      {/* Action Button */}
                      <div className="border-t border-slate-100/50 pt-3 flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => navigate(actionPath)}
                          className="rounded-lg text-[10px] font-extrabold h-8 px-3 shrink-0 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 hover:text-slate-900 shadow-xs"
                        >
                          {t("performanceBySection.practiceThisSection", "Practice")}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })()}

      {/* WEAKNESS MAP CARD BLOCK */}
      {personalGoals && (() => {
        const getSeverityBadge = (sev: string) => {
          if (sev === "Critical") {
            return <Badge className="text-[10px] font-bold bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-50">{t("weaknessMap.severity.critical", "Critical")}</Badge>;
          }
          if (sev === "Needs Attention") {
            return <Badge className="text-[10px] font-bold bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-50">{t("weaknessMap.severity.needsAttention", "Needs Attention")}</Badge>;
          }
          if (sev === "On Track") {
            return <Badge className="text-[10px] font-bold bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-50">{t("weaknessMap.severity.onTrack", "On Track")}</Badge>;
          }
          return <Badge className="text-[10px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-50">{t("weaknessMap.severity.strong", "Strong")}</Badge>;
        };

        const getCompetenceIcon = (comp: string) => {
          if (comp === "Reading") return <BookOpen className="w-4 h-4 text-purple-500 shrink-0" />;
          if (comp === "Listening") return <Headphones className="w-4 h-4 text-blue-500 shrink-0" />;
          return <PenTool className="w-4 h-4 text-emerald-500 shrink-0" />;
        };

        const getCompetenceLabel = (comp: string) => {
          if (comp === "Reading") return "Reading (CE)";
          if (comp === "Listening") return "Listening (CO)";
          if (comp === "Writing") return "Writing (EE)";
          return comp;
        };

        return (
          <Card className="border-none shadow-sm bg-white rounded-2xl p-6 text-left">
            {/* Header */}
            <div className={`flex items-center justify-between gap-4 ${isWeaknessMapExpanded ? "border-b border-slate-100 pb-4 mb-5" : ""}`}>
              <div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight">{t("weaknessMap.title", "Weakness Map")}</h3>
                <p className="text-slate-500 text-xs font-semibold mt-0.5">{t("weaknessMap.subtitle", "Your main areas to improve")}</p>
              </div>

              {/* Toggle Expand/Collapse Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleWeaknessMap}
                className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 shrink-0"
                aria-label={isWeaknessMapExpanded ? "Collapse" : "Expand"}
              >
                {isWeaknessMapExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>

            {isWeaknessMapExpanded && (
              <>
                {/* Content list */}
                {!hasEnoughDataForWeaknessMap || activeWeaknesses.length === 0 ? (
                  <div className="py-8 text-center text-slate-400 italic text-xs font-semibold">
                    {t("weaknessMap.notEnoughDataDesc", "Not enough data yet. Complete more tests to generate your weakness map.")}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeWeaknesses.map((weak, index) => (
                      <div 
                        key={weak.id || index}
                        className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100 flex flex-col justify-between gap-4 hover:border-primary/20 hover:bg-white transition-all duration-200"
                      >
                        <div>
                          {/* Header containing name and severity */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2.5 min-w-0">
                              <div className="w-8 h-8 rounded-xl bg-white border border-slate-200/50 flex items-center justify-center shrink-0 shadow-xs">
                                {getCompetenceIcon(weak.competence)}
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-extrabold text-xs text-slate-800 leading-snug truncate" title={weak.name}>
                                  {weak.name}
                                </h4>
                                <span className="text-[10px] text-slate-400 font-bold">
                                  {getCompetenceLabel(weak.competence)}
                                </span>
                              </div>
                            </div>
                            {getSeverityBadge(weak.severity)}
                          </div>

                          {/* Detail / Reason */}
                          <div className="mt-4 text-[11px] font-semibold text-slate-500 border-t border-slate-100/50 pt-3">
                            <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold block mb-1">
                              {t("weaknessMap.reason", "Reason")}:
                            </span>
                            <span className="font-black text-slate-700 capitalize">
                              {weak.reason}
                            </span>
                          </div>
                        </div>

                        {/* Action Button */}
                        <div className="border-t border-slate-100/50 pt-3 flex justify-end">
                          <Button
                            size="sm"
                            onClick={() => navigate(weak.actionPath)}
                            className="rounded-lg text-[10px] font-extrabold h-8 px-3 shrink-0 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 hover:text-slate-900 shadow-xs"
                          >
                            {weak.actionLabel}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>
        );
      })()}

      {/* RECOMMENDATIONS, STUDY PLAN & SUMMARY */}
      {(() => {
        const parseNclcToNumber = (nclcStr: string | number | undefined): number => {
          if (nclcStr === undefined || nclcStr === null) return 0;
          if (typeof nclcStr === "number") return nclcStr;
          const str = String(nclcStr).trim();
          if (str.includes("<")) return 2;
          if (str.includes("+")) return 10;
          const parsed = parseInt(str);
          return isNaN(parsed) ? 0 : parsed;
        };

        const targetCeNclc = personalGoals?.targetCeNclc ?? parseNclcToNumber(readingNclcGoal) ?? 7;
        const targetCoNclc = personalGoals?.targetCoNclc ?? parseNclcToNumber(listeningNclcGoal) ?? 7;
        const currentCeNclc = readingLevelInfo ? parseNclcToNumber(readingLevelInfo.nclc) : 0;
        const currentCoNclc = listeningLevelInfo ? parseNclcToNumber(listeningLevelInfo.nclc) : 0;

        const dynamicRecs: any[] = [];

        // Priority 1: Top Weakness from Weakness Map
        if (activeWeaknesses.length > 0) {
          const topWeak = activeWeaknesses[0];
          
          let recTitle = "";
          let recDesc = "";
          let recActionLabel = topWeak.actionLabel;
          
          if (topWeak.type === "section") {
            recTitle = `${t("performanceBySection.practiceThisSection", "Practice")} ${topWeak.name}`;
            recDesc = t("weaknessMap.recs.weakestDesc", "This is currently your weakest {{competence}} section: {{name}}.", { competence: topWeak.competence, name: topWeak.name });
            recActionLabel = topWeak.competence === "Reading" ? t("performanceBySection.takeReadingTest", "Take Reading Test") : 
                             topWeak.competence === "Listening" ? t("performanceBySection.takeListeningTest", "Take Listening Test") : 
                             t("performanceBySection.startWriting", "Start Writing");
          } else if (topWeak.type === "writing-category") {
            recTitle = `${t("weaknessMap.actions.reviewErrorBank", "Review Error Bank")}: ${topWeak.name}`;
            recDesc = t("weaknessMap.recs.errorBankDesc", "This is currently your most frequent writing issue: {{name}}.", { name: topWeak.name });
            recActionLabel = t("weaknessMap.actions.reviewErrorBank", "Review Error Bank");
          } else if (topWeak.type === "flashcards") {
            recTitle = t("weaknessMap.actions.studyNow", "Review Flashcards");
            recDesc = t("weaknessMap.recs.flashcardsDesc", "You have {{count}} flashcards due today. Keep up your streak!", { count: topWeak.volume });
            recActionLabel = t("weaknessMap.actions.studyNow", "Review Flashcards");
          } else {
            recTitle = t("weaknessMap.actions.takeSimulation", "Take Full Simulation");
            recDesc = t("weaknessMap.recs.simulationsDesc", "Improve your exam pacing and overall strategy with a complete simulation.");
            recActionLabel = t("weaknessMap.actions.takeSimulation", "Take Full Simulation");
          }

          dynamicRecs.push({
            id: "top-weakness",
            title: recTitle,
            description: recDesc,
            priorityLabel: t("performanceBySection.priority1", "Priority 1"),
            priorityColor: "bg-rose-50 text-rose-700 border-rose-100",
            priorityBullet: "bg-rose-500 animate-pulse",
            actionLabel: recActionLabel,
            actionPath: topWeak.actionPath,
            priorityNum: 0.5
          });
        }

        // Priority 1: Improve Reading (if CE below target)
        if (hasReadingData && currentCeNclc < targetCeNclc) {
          dynamicRecs.push({
            id: "improve-reading",
            title: "Improve Reading",
            description: "Your Reading score is below your target.",
            priorityLabel: "Priority 1",
            priorityColor: "bg-rose-50 text-rose-700 border-rose-100",
            priorityBullet: "bg-rose-500 animate-pulse",
            actionLabel: "Take Reading Test",
            actionPath: "/start-test?competence=reading",
            priorityNum: 1
          });
        }

        // Priority 1.5: Improve Listening (if CO below target)
        if (hasListeningData && currentCoNclc < targetCoNclc) {
          dynamicRecs.push({
            id: "improve-listening",
            title: "Improve Listening",
            description: "Your Listening score is below your target.",
            priorityLabel: "Priority 1.5",
            priorityColor: "bg-rose-50 text-rose-700 border-rose-100",
            priorityBullet: "bg-rose-500 animate-pulse",
            actionLabel: "Take Listening Test",
            actionPath: "/start-test?competence=listening",
            priorityNum: 1.5
          });
        }

        // Priority 2: Review Error Bank (if recurring errors > 0)
        if (writingErrors.length > 0) {
          dynamicRecs.push({
            id: "review-error-bank",
            title: "Review Error Bank",
            description: `You have ${writingErrors.length} recurring ${writingErrors.length === 1 ? "error" : "errors"} registered.`,
            priorityLabel: "Priority 2",
            priorityColor: "bg-amber-50 text-amber-700 border-amber-100",
            priorityBullet: "bg-amber-500",
            actionLabel: "Open Error Bank",
            actionPath: "/writing-errors",
            priorityNum: 2
          });
        }

        // Priority 3: Review Flashcards (if pending flashcards due > 0)
        if (pendingFlashcardsCount > 0) {
          dynamicRecs.push({
            id: "review-flashcards",
            title: "Review Flashcards",
            description: `${pendingFlashcardsCount} ${pendingFlashcardsCount === 1 ? "flashcard is" : "flashcards are"} due today.`,
            priorityLabel: "Priority 3",
            priorityColor: "bg-indigo-50 text-indigo-700 border-indigo-100",
            priorityBullet: "bg-indigo-500",
            actionLabel: "Study Now",
            actionPath: "/flashcards",
            priorityNum: 3
          });
        }

        // Priority 4: Practice Writing (Always available)
        dynamicRecs.push({
          id: "practice-writing",
          title: "Practice Writing",
          description: "Continue building your writing history.",
          priorityLabel: "Priority 4",
          priorityColor: "bg-slate-100 text-slate-700 border-slate-200",
          priorityBullet: "bg-slate-500",
          actionLabel: "Start Writing",
          actionPath: "/start-test?competence=writing",
          priorityNum: 4
        });

        // Dynamic Fallbacks to guarantee exactly 4 recommendations if lists are short
        if (dynamicRecs.length < 4) {
          if (!hasReadingData) {
            dynamicRecs.push({
              id: "improve-reading",
              title: "Improve Reading",
              description: "Start your Reading preparation.",
              priorityLabel: "Priority 1",
              priorityColor: "bg-rose-50 text-rose-700 border-rose-100",
              priorityBullet: "bg-rose-500",
              actionLabel: "Take Reading Test",
              actionPath: "/start-test?competence=reading",
              priorityNum: 1
            });
          }
          if (!hasListeningData && dynamicRecs.length < 4) {
            dynamicRecs.push({
              id: "improve-listening",
              title: "Improve Listening",
              description: "Start your Listening preparation.",
              priorityLabel: "Priority 1.5",
              priorityColor: "bg-rose-50 text-rose-700 border-rose-100",
              priorityBullet: "bg-rose-500",
              actionLabel: "Take Listening Test",
              actionPath: "/start-test?competence=listening",
              priorityNum: 1.5
            });
          }
          if (flashcards.length === 0 && dynamicRecs.length < 4) {
            dynamicRecs.push({
              id: "explore-flashcards",
              title: "Review Flashcards",
              description: "Practice flashcards to build vocabulary.",
              priorityLabel: "Priority 3",
              priorityColor: "bg-indigo-50 text-indigo-700 border-indigo-100",
              priorityBullet: "bg-indigo-500",
              actionLabel: "Study Now",
              actionPath: "/flashcards",
              priorityNum: 3
            });
          }
        }

        // Sort by priorityNum and slice to 4
        const sortedRecs = dynamicRecs
          .sort((a, b) => a.priorityNum - b.priorityNum)
          .slice(0, 4);

        const studyPlan = generateStudyPlan(sortedRecs, t);

        return (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* RECOMMENDATIONS */}
            <Card className="border-none shadow-sm bg-white rounded-2xl p-6 flex flex-col justify-between text-left">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
                  <h3 className="text-base font-bold text-gray-900">{t("dashboard.smartRecommendations", "Recomendações Inteligentes")}</h3>
                </div>
                <div className="space-y-3">
                  {sortedRecs.map((rec, index) => (
                    <div key={rec.id || index} className="p-3.5 bg-gray-50/80 rounded-xl border border-gray-100 flex items-center justify-between gap-3 hover:border-primary/20 transition-all duration-200 text-left">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${rec.priorityBullet}`} />
                          <h4 className="font-bold text-xs text-gray-900 truncate">{rec.title}</h4>
                        </div>
                        <p className="text-[11px] text-gray-500 line-clamp-2 leading-relaxed">{rec.description}</p>
                        <div className="mt-1">
                          <Badge className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md ${rec.priorityColor}`}>
                            {rec.priorityLabel}
                          </Badge>
                        </div>
                      </div>
                      <Button size="sm" asChild className="rounded-lg text-[10px] font-bold h-7 px-2.5 shrink-0 bg-primary hover:bg-primary/90 text-white shadow-xs">
                        <Link to={rec.actionPath}>
                          <span>{rec.actionLabel}</span>
                        </Link>
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* PLANO DE ESTUDO DE HOJE */}
            <Card id="todays-study-plan" className="border-none shadow-sm bg-white rounded-2xl p-6 flex flex-col justify-between text-left">
              <div>
                <div className="flex flex-col gap-1 mb-4 border-b border-slate-100 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-primary shrink-0" />
                      <h3 className="text-base font-bold text-gray-900 leading-none">{t("weaknessMap.todayStudyPlan.title", "Today’s Study Plan")}</h3>
                    </div>
                    {/* Progress Badge */}
                    {(() => {
                      const completedCount = dailyStudyPlanTasks.filter(t => completedTasks[t.id]).length;
                      const totalCount = dailyStudyPlanTasks.length;
                      return (
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                          {completedCount} / {totalCount} {t("weaknessMap.todayStudyPlan.completed", "Completed")}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Subtitle */}
                  <p className="text-[10px] text-slate-400 font-bold mt-1">
                    {t("weaknessMap.todayStudyPlan.subtitle", "Your personalized tasks for today")}
                  </p>
                  {/* Progress Bar */}
                  {(() => {
                    const completedCount = dailyStudyPlanTasks.filter(t => completedTasks[t.id]).length;
                    const totalCount = dailyStudyPlanTasks.length;
                    const percentComplete = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
                    return (
                      <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mt-3">
                        <div className="bg-primary h-full rounded-full transition-all duration-300" style={{ width: `${percentComplete}%` }} />
                      </div>
                    );
                  })()}
                </div>

                {!hasEnoughDataForWeaknessMap || dailyStudyPlanTasks.length === 0 ? (
                  <div className="py-8 text-center text-slate-400 italic text-xs font-semibold">
                    {t("weaknessMap.todayStudyPlan.completeMoreTests", "Complete more tests to generate a personalized plan")}
                  </div>
                ) : (() => {
                  const sortedPlanTasks = [...dailyStudyPlanTasks].sort((a, b) => {
                    const aDone = completedTasks[a.id] ? 1 : 0;
                    const bDone = completedTasks[b.id] ? 1 : 0;
                    return aDone - bDone;
                  });

                  return (
                    <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                      {sortedPlanTasks.map((plan) => {
                        const isDone = !!completedTasks[plan.id];
                        return (
                          <div 
                            key={plan.id} 
                            className={`p-3 bg-slate-50/50 rounded-2xl border flex flex-col gap-2 hover:bg-white hover:border-primary/20 transition-all duration-200 text-left ${isDone ? "opacity-60 border-slate-200 bg-slate-100/30" : "border-slate-100"}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              {/* Left: Checkbox + Title */}
                              <div className="flex items-start gap-2 min-w-0">
                                <button 
                                  onClick={() => toggleTaskCompletion(plan.id)}
                                  className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 mt-0.5 transition-all ${isDone ? "bg-primary border-primary text-white" : "border-slate-300 hover:border-primary bg-white"}`}
                                  title={t("weaknessMap.todayStudyPlan.markAsDone", "Mark as done")}
                                >
                                  {isDone && (
                                    <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 20 20">
                                      <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
                                    </svg>
                                  )}
                                </button>
                                <div className="min-w-0">
                                  <h4 className={`font-extrabold text-[11px] text-slate-800 leading-snug truncate ${isDone ? "line-through text-slate-400" : ""}`} title={plan.title}>
                                    {plan.title}
                                  </h4>
                                  <p className="text-[10px] text-slate-400 font-bold leading-relaxed line-clamp-2 mt-0.5">
                                    {plan.description}
                                  </p>
                                  {!isDone && (() => {
                                    const taskProgress = getTaskProgress(plan);
                                    if (taskProgress.progressPercent <= 0) return null;
                                    return (
                                      <div className="space-y-1 mt-1.5">
                                        <div className="flex justify-between text-[9px] font-bold text-primary">
                                          <span>{t("studyTime.progress", "Progresso")}</span>
                                          <span>{taskProgress.studiedMinutes}m / {taskProgress.estMinutes}m ({taskProgress.progressPercent}%)</span>
                                        </div>
                                        <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden">
                                          <div className="bg-primary h-full rounded-full transition-all duration-300" style={{ width: `${taskProgress.progressPercent}%` }} />
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>

                              </div>
                            </div>

                            {/* Details and Actions */}
                            <div className="flex items-center justify-between gap-2 border-t border-slate-100/50 pt-2 text-[9px] font-bold text-slate-400">
                              <div className="flex items-center gap-2">
                                <span>{plan.priorityLabel}</span>
                                <span>•</span>
                                <span>{t("weaknessMap.todayStudyPlan.estimatedTime", "Time")}: {plan.estimatedTime}</span>
                              </div>
                              
                              <Button 
                                size="sm" 
                                asChild 
                                disabled={isDone}
                                className={`rounded-lg text-[9px] font-black h-6 px-2.5 shrink-0 shadow-xs ${isDone ? "bg-slate-200 text-slate-400 cursor-not-allowed hover:bg-slate-200" : "bg-primary hover:bg-primary/90 text-white"}`}
                              >
                                <Link to={isDone ? "#" : plan.actionPath}>
                                  <span>{plan.actionLabel}</span>
                                </Link>
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </Card>

            {/* RESUMO DO DESEMPENHO */}
            <Card className="border-none shadow-sm bg-white rounded-2xl p-6 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="w-5 h-5 text-purple-500" />
                  <h3 className="text-base font-bold text-gray-900">{t("dashboard.performanceSummary", "Resumo do Desempenho")}</h3>
                </div>
                {historyItems.length === 0 ? (
                  <div className="text-center py-8 text-xs text-gray-400 italic bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                    {t("dashboard.completeTestsToGenerateSummary", "Complete alguns testes para gerar um resumo de desempenho.")}
                  </div>
                ) : (() => {
                  const bestCe = getBestSkillResult(historyItems, "ce")?.nclc ?? 0;
                  const bestCo = getBestSkillResult(historyItems, "co")?.nclc ?? 0;

                  const arr = [
                    { name: "Compréhension Écrite (CE)", val: bestCe },
                    { name: "Compréhension Orale (CO)", val: bestCo }
                  ];

                  arr.sort((a, b) => b.val - a.val);
                  const best = arr[0].val > 0 ? arr[0].name : t("dashboard.none", "Nenhuma");
                  const worst = arr[1].val > 0 ? arr[1].name : "Compréhension Orale (CO)";

                  let lastAct = t("dashboard.noActivity", "Sem atividade");
                  if (historyItems.length > 0) {
                    try {
                      lastAct = new Date(historyItems[0].date).toLocaleDateString(i18n.language);
                    } catch {}
                  }

                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3.5 bg-green-50/70 rounded-xl border border-green-100">
                        <span className="text-xs font-bold text-green-900">{t("dashboard.bestPerformance", "Melhor desempenho")}:</span>
                        <span className="text-xs font-bold text-green-700">{best}</span>
                      </div>

                      <div className="flex items-center justify-between p-3.5 bg-red-50/70 rounded-xl border border-red-100">
                        <span className="text-xs font-bold text-red-900">{t("dashboard.highestPriority", "Maior prioridade")}:</span>
                        <span className="text-xs font-bold text-red-700">{worst}</span>
                      </div>

                      <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                        <span className="text-xs font-bold text-gray-700">{t("dashboard.testsPerformed", "Testes realizados")}:</span>
                        <span className="text-xs font-black text-gray-900 font-mono">{historyItems.length}</span>
                      </div>

                      <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                        <span className="text-xs font-bold text-gray-700">{t("dashboard.lastActivity", "Última atividade")}:</span>
                        <span className="text-xs font-bold text-gray-500 font-mono">{lastAct}</span>
                      </div>

                      <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200/60 text-[11px] text-amber-800 font-bold">
                        <Layers className="w-4 h-4 text-amber-600 shrink-0" />
                        <span>{t("dashboard.pendingFlashcardsCountMsg", "Você tem {{count}} flashcards pendentes no SRS.", { count: pendingFlashcardsCount })}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </Card>
          </div>
        );
      })()}

      {/* AI TEF COACH / DIAGNÓSTICO INTELIGENTE */}
      <Card className="border-none shadow-lg bg-gradient-to-br from-indigo-900 via-indigo-950 to-slate-950 text-white rounded-3xl p-8 text-left relative overflow-hidden my-8 max-w-4xl mx-auto w-full">
        {/* Glow/Sparkles background */}
        <div className="absolute top-0 right-0 -mt-12 -mr-12 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-80 h-80 bg-violet-600/15 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div className="space-y-4 flex-1">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-indigo-500/15 border border-indigo-400/20 text-indigo-300 rounded-xl">
                <Sparkles className="w-5 h-5 fill-indigo-300 animate-pulse" />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight flex items-center gap-2 text-white">
                  <span>Coach TEF IA</span>
                  <Badge className="bg-amber-400 text-slate-950 font-extrabold text-[9px] px-2 py-0.5 border-none tracking-wider uppercase">Beta Premium</Badge>
                </h3>
                <p className="text-[10px] text-indigo-200/60 font-bold uppercase tracking-widest mt-0.5">Diagnóstico qualitativo de estudos</p>
              </div>
            </div>

            <div className="space-y-4 text-sm text-slate-100/90 leading-relaxed font-medium font-sans">
              {isRefreshingCoach ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
                  <p className="text-xs text-indigo-200 font-bold animate-pulse">Analisando métricas qualitativas e recalculando rota...</p>
                </div>
              ) : (
                (coachTextOverride || generateTefCoachInsight()).split("\n\n").map((para, index) => (
                  <p key={index} className="text-slate-100/95">{para}</p>
                ))
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 shrink-0 self-stretch md:self-auto justify-end">
            <Button
              onClick={() => {
                setIsRefreshingCoach(true);
                setTimeout(() => {
                  setIsRefreshingCoach(false);
                  setCoachTextOverride(generateTefCoachInsight());
                  showToast(t("dashboard.metricsUpdatedByCoach", "Métricas atualizadas com sucesso pelo Coach!"));
                }, 450);
              }}
              disabled={isRefreshingCoach}
              variant="outline"
              className="h-11 border-indigo-500/30 text-indigo-200 hover:text-white bg-indigo-950/40 hover:bg-indigo-900/60 font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shrink-0"
            >
              <RefreshCw className={`w-3.5 h-3.5 shrink-0 ${isRefreshingCoach ? "animate-spin" : ""}`} />
              <span>{t("dashboard.updateDiagnosis", "Atualizar Diagnóstico")}</span>
            </Button>

            <Button
              onClick={() => {
                document.getElementById("todays-study-plan")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="h-11 bg-amber-400 hover:bg-amber-350 text-slate-950 font-black rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-lg shadow-amber-400/10 hover:scale-[1.01] transition-all border-none"
            >
              <span>{t("dashboard.followTodaysPlan", "Seguir Plano de Hoje")}</span>
              <ArrowRight className="w-4 h-4 text-slate-950 shrink-0" />
            </Button>
          </div>
        </div>
      </Card>



      {/* EXPRESSION ÉCRITE - PROGRESSO PEDAGÓGICO */}
      <div className="space-y-6 pt-10 mt-10 border-t border-slate-100">
        <div className="flex flex-col gap-1.5 pb-4">
          <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
            <PenTool className="w-5 h-5 text-emerald-600 animate-pulse" />
            <span>{t("dashboard.pedagogicalCorrectionTitle", "Expression Écrite — Progresso Pedagógico")}</span>
          </h2>
          <p className="text-xs text-slate-500">
            {t("dashboard.pedagogicalCorrectionDesc", "Métricas qualitativas baseadas na correção de desvios e análise pedagógica por IA.")}
          </p>
        </div>

        {totalCorrections === 0 ? (
          <Card className="border-none shadow-sm bg-white rounded-2xl p-8 text-center border border-slate-100">
            <div className="max-w-md mx-auto space-y-4">
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-600">
                <Sparkles className="w-6 h-6 animate-pulse" />
              </div>
              <h3 className="text-base font-bold text-slate-800">{t("dashboard.noPedagogicalStats", "Ainda não há estatísticas pedagógicas")}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                {t("dashboard.noPedagogicalStatsDesc", "Faça uma correção de Expression Écrite para começar a gerar estatísticas pedagógicas e identificar seus principais focos de estudo.")}
              </p>
              <Button asChild className="rounded-xl font-bold text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                <Link to="/start-test?type=writing_v2">
                  <span>{t("dashboard.startRedactionButton", "Iniciar Prática de Redação")}</span>
                </Link>
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Linha de Cards Principais */}
            <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
              <Card className="border-none shadow-sm bg-white p-5 rounded-2xl border border-indigo-50 flex flex-col justify-between">
                <div>
                  <span className="text-[10px] text-indigo-600 font-extrabold uppercase tracking-wider block mb-1">
                    {t("dashboard.completedCorrections", "Correções Realizadas")}
                  </span>
                  <p className="text-3xl font-black text-indigo-950 font-mono">
                    {totalCorrections}
                  </p>
                </div>
                <p className="text-[10px] text-indigo-400 mt-2 font-semibold">{t("dashboard.evaluatedEssays", "Redações avaliadas")}</p>
              </Card>

              <Card className="border-none shadow-sm bg-white p-5 rounded-2xl border border-rose-50 flex flex-col justify-between">
                <div>
                  <span className="text-[10px] text-rose-600 font-extrabold uppercase tracking-wider block mb-1">
                    {t("dashboard.identifiedErrors", "Erros Identificados")}
                  </span>
                  <p className="text-3xl font-black text-rose-950 font-mono">
                    {totalErrors}
                  </p>
                </div>
                <p className="text-[10px] text-rose-400 mt-2 font-semibold">{t("dashboard.totalGrammarErrors", "Desvios gramaticais totais")}</p>
              </Card>

              <Card className="border-none shadow-sm bg-white p-5 rounded-2xl border border-teal-50 flex flex-col justify-between">
                <div>
                  <span className="text-[10px] text-teal-600 font-extrabold uppercase tracking-wider block mb-1">
                    {t("dashboard.averageWords", "Média de Palavras")}
                  </span>
                  <p className="text-3xl font-black text-teal-950 font-mono">
                    {averageWords}
                  </p>
                </div>
                <p className="text-[10px] text-teal-400 mt-2 font-semibold">{t("dashboard.averageExtension", "Extensão média")}</p>
              </Card>

              <Card className="border-none shadow-sm bg-white p-5 rounded-2xl border border-amber-50 flex flex-col justify-between">
                <div>
                  <span className="text-[10px] text-amber-600 font-extrabold uppercase tracking-wider block mb-1">
                    {t("dashboard.mainFocus", "Principal Foco")}
                  </span>
                  <p className="text-sm font-black text-slate-800 truncate mt-2">
                    {topFocusCategory !== "N/A" ? translateErrorCategory(topFocusCategory, t) : t("dashboard.general", "Geral")}
                  </p>
                </div>
                <p className="text-[10px] text-amber-400 mt-2 font-semibold">{t("dashboard.mostFrequentCategory", "Categoria mais frequente")}</p>
              </Card>
            </div>

            {/* Grid Detalhado de Performance */}
            <div className="grid gap-6 md:grid-cols-2">
              {/* Desempenho por Seção */}
              <Card className="border-none shadow-sm bg-white p-6 rounded-2xl border border-slate-100 flex flex-col justify-between">
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-slate-800 border-b border-slate-50 pb-2">
                    {t("dashboard.performanceBySection", "Desempenho por Seção")}
                  </h3>
                  <div className="grid gap-4 grid-cols-2">
                    {/* Section A */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-black text-slate-800">Section A</span>
                        <Badge className="bg-indigo-50 border-indigo-100 text-indigo-700 text-[9px] font-bold">Fait divers</Badge>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                          <span>{t("dashboard.corrections", "Correções:")}</span>
                          <span className="font-mono text-slate-800 font-bold">{secACount}</span>
                        </div>
                        <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                          <span>{t("dashboard.avgWords", "Média palavras:")}</span>
                          <span className="font-mono text-slate-800 font-bold">{secAAverageWords}</span>
                        </div>
                        <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                          <span>{t("dashboard.avgErrors", "Média erros:")}</span>
                          <span className="font-mono text-slate-800 font-bold">{secAAverageErrors}</span>
                        </div>
                        <div className="flex justify-between text-[11px] text-slate-500 font-medium min-w-0">
                          <span className="shrink-0 text-slate-500">{t("dashboard.focus", "Foco:")}</span>
                          <span className="text-slate-800 font-bold truncate ml-1">{translateErrorCategory(secAFrequentCategory, t)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Section B */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-black text-slate-800">Section B</span>
                        <Badge className="bg-purple-50 border-purple-100 text-purple-700 text-[9px] font-bold">Lettre formelle</Badge>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                          <span>{t("dashboard.corrections", "Correções:")}</span>
                          <span className="font-mono text-slate-800 font-bold">{secBCount}</span>
                        </div>
                        <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                          <span>{t("dashboard.avgWords", "Média palavras:")}</span>
                          <span className="font-mono text-slate-800 font-bold">{secBAverageWords}</span>
                        </div>
                        <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                          <span>{t("dashboard.avgErrors", "Média erros:")}</span>
                          <span className="font-mono text-slate-800 font-bold">{secBAverageErrors}</span>
                        </div>
                        <div className="flex justify-between text-[11px] text-slate-500 font-medium min-w-0">
                          <span className="shrink-0 text-slate-500">{t("dashboard.focus", "Foco:")}</span>
                          <span className="text-slate-800 font-bold truncate ml-1">{translateErrorCategory(secBFrequentCategory, t)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Erros Mais Frequentes */}
              <Card className="border-none shadow-sm bg-white p-6 rounded-2xl border border-slate-100 flex flex-col justify-between">
                <div className="space-y-4 w-full">
                  <h3 className="text-sm font-bold text-slate-800 border-b border-slate-50 pb-2">
                    {t("dashboard.frequentErrors", "Erros Mais Frequentes (Top 5)")}
                  </h3>
                  {sortedCategories.length === 0 ? (
                    <p className="text-xs text-slate-400 italic py-6 text-center">{t("dashboard.noErrorsCategorized", "Nenhum desvio categorizado ainda.")}</p>
                  ) : (
                    <div className="space-y-2.5">
                      {sortedCategories.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 text-[10px] font-black text-slate-500">
                              {idx + 1}
                            </span>
                            <span className="text-xs font-bold text-slate-700 truncate">{translateErrorCategory(item.category, t)}</span>
                          </div>
                          <Badge className="bg-rose-50 border-rose-100 text-rose-700 font-bold text-[10px]">
                            {item.count} {item.count === 1 ? t("common.error", "erro") : t("common.errors", "erros")}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </div>


            {/* Últimas Correções EE */}
            <Card className="border-none shadow-sm bg-white p-6 rounded-2xl border border-slate-100">
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-slate-800 border-b border-slate-50 pb-2">
                  {t("dashboard.recentEeCorrections", "Últimas Correções EE")}
                </h3>
                {eeV2Records.length === 0 ? (
                  <p className="text-xs text-slate-400 italic text-center py-6">{t("dashboard.noRecentEeCorrections", "Nenhuma correção recente.")}</p>
                ) : (
                  <div className="space-y-3">
                    {eeV2Records.slice(0, 5).map((record) => {
                      let dateStr = t("common.recently", "Recentemente");
                      try {
                        dateStr = new Date(record.date).toLocaleDateString(t("common.localeCode", "pt-BR"), {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric"
                        });
                      } catch {}

                      const section = getWritingV2Section(record);
                      const isCompleteTest = record.metadata?.testMode === "complete" || record.metadata?.isCompleteTest;
                      const typeLabel = isCompleteTest ? t("dashboard.completeEeTest", "Teste completo EE") : t("dashboard.individualPractice", "Prática Individual");
                      
                      const wordCount = getWritingV2WordCount(record);
                      const errorCount = getWritingV2ErrorCount(record);
                      const focusAreas = getWritingV2FocusAreas(record);

                      return (
                        <div key={record.id} className="p-4 bg-slate-50 hover:bg-slate-100/50 rounded-xl border border-slate-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4 transition-all duration-200">
                          <div className="flex-1 space-y-1.5 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-emerald-100 text-emerald-800 border-none text-[10px] font-bold py-0.5">
                                {section}
                              </Badge>
                              <Badge className="bg-blue-50 border-blue-150 text-blue-800 text-[10px] font-medium py-0.5">
                                {typeLabel}
                              </Badge>
                              <span className="text-[10px] text-slate-400 font-semibold">{dateStr}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-600 font-semibold">
                              <span>{wordCount} {t("dashboard.words", "palavras")}</span>
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                              <span className="text-rose-600 font-bold">{errorCount} {t("dashboard.deviations", "desvios")}</span>
                            </div>
                            {focusAreas.length > 0 && (
                              <p className="text-[10px] text-slate-400 truncate">
                                {t("dashboard.focusLabel", "Focos")}: {focusAreas.join(", ")}
                              </p>
                            )}
                          </div>
                          <div className="shrink-0 flex items-center">
                            <Button
                              onClick={() => {
                                const correctionId = record.id;
                                const attemptId = record.metadata?.testAttemptId || record.id;
                                const testId = record.metadata?.testId || record.id;
                                const targetPath = "/test/result";
                                const item = record;

                                console.log("[EE_RECENT_REVIEW_CLICK]", {
                                  correctionId,
                                  attemptId,
                                  testId,
                                  targetPath,
                                  item,
                                });

                                const correction = record.metadata?.writingCorrection;
                                if (!record || !correction) {
                                  showToast(t("dashboard.correctionNotFound", "Correção não encontrada. Abra o Histórico para ver suas correções salvas."));
                                  return;
                                }

                                navigate(targetPath, {
                                  state: {
                                    fromHistory: true,
                                    resultType: "writing_v2",
                                    writingVersion: 2,
                                    testType: "writing_v2_history_review",
                                    historyRecord: record,
                                    writingTask: record.metadata?.writingTask,
                                    writingCorrection: correction,
                                    writingTasks: record.metadata?.writingTask ? [record.metadata.writingTask] : []
                                  }
                                });
                              }}
                              className="w-full md:w-auto h-8 px-4 font-bold text-xs bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg shadow-none shrink-0"
                            >
                              {t("common.review", "Revisar")}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
        
        {/* Line Chart - Evolução */}
        <Card className="col-span-1 lg:col-span-7 border-none shadow-sm bg-white rounded-2xl overflow-hidden">
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="text-base font-bold text-gray-900">{t("dashboard.scoreEvolutionTitle", "Evolução de Notas")}</CardTitle>
              <CardDescription className="text-xs text-gray-400">{t("dashboard.clickPointDetail", "Clique em um ponto para abrir detalhes da simulação")}</CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsChartExpanded(true)}
              className="h-8 rounded-lg gap-1.5 font-bold text-xs text-primary hover:text-primary/90 hover:bg-primary/5 px-2.5"
            >
              <Maximize2 className="w-4 h-4" />
              {t("dashboard.viewLarger", "Ver maior")}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full overflow-visible">
              {!hasData ? (
                renderEmptyChartPlaceholder()
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={progressData} 
                    margin={{ top: 56, right: 48, bottom: 10, left: 10 }}
                    style={{ overflow: "visible" }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#6b7280', fontSize: 11}} dy={10} padding={{ left: 48, right: 48 }} />
                    <YAxis domain={[0, yAxisMax]} allowDataOverflow={false} axisLine={false} tickLine={false} tick={{fill: '#6b7280', fontSize: 11}} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.05), 0 4px 6px -4px rgb(0 0 0 / 0.05)' }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', paddingTop: '15px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="reading" 
                      stroke="#8b5cf6" 
                      strokeWidth={3} 
                      dot={<CustomClickableDot dataKey="reading" />}
                      activeDot={<CustomClickableDot dataKey="reading" />}
                      name={t("dashboard.ceScale", "Compréhension Écrite - CE (Escala 300)")} 
                      connectNulls 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="listening" 
                      stroke="#3b82f6" 
                      strokeWidth={3} 
                      dot={<CustomClickableDot dataKey="listening" />}
                      activeDot={<CustomClickableDot dataKey="listening" />}
                      name={t("dashboard.coScale", "Compréhension Orale - CO (Escala 360)")} 
                      connectNulls 
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bar Chart - Erros */}
        <Card className="col-span-1 lg:col-span-4 border-none shadow-sm bg-white rounded-2xl overflow-hidden">
          <CardHeader>
            <CardTitle className="text-base font-bold text-gray-900">{t("dashboard.writingFrequentErrors", "Erros Frequentes (Writing)")}</CardTitle>
            <CardDescription className="text-xs text-gray-400">{t("dashboard.clickBarFilterErrors", "Clique na barra de categoria para filtrar Banco de Erros")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] w-full">
              {!hasWritingErrors ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6 bg-gray-50/30 rounded-2xl border border-dashed border-gray-100">
                  <AlertCircle className="w-8 h-8 mb-2 stroke-[1.2] text-gray-300 animate-pulse" />
                  <span className="text-xs font-bold text-gray-500">{t("dashboard.noErrorsRecordedYet", "Nenhum erro registrado ainda.")}</span>
                  <span className="text-[10px] text-gray-400 mt-0.5 leading-relaxed max-w-[220px]">
                    {t("dashboard.savedErrorsWillAppear", "Erros salvos das suas redações aparecerão listados aqui.")}
                  </span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={errorsByCategory} margin={{ top: 5, right: 20, bottom: 5, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#6b7280', fontSize: 10}} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#6b7280', fontSize: 11}} />
                    <Tooltip cursor={{fill: '#f9fafb'}} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.05)' }} />
                    <Bar dataKey="count" fill="#f43f5e" radius={[4, 4, 0, 0]} name={t("dashboard.occurrences", "Ocorrências")} tabIndex={-1}>
                      {errorsByCategory.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill="#f43f5e" 
                          className="cursor-pointer hover:opacity-80 transition-opacity focus:outline-none"
                          tabIndex={-1}
                          onClick={() => {
                            navigate(`/writing-errors?category=${encodeURIComponent(entry.name)}`);
                          }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart - Flashcards */}
        <Card className="col-span-1 lg:col-span-3 border-none shadow-sm bg-white rounded-2xl overflow-hidden">
          <CardHeader>
            <CardTitle className="text-base font-bold text-gray-900">{t("dashboard.flashcardsTitle", "Flashcards")}</CardTitle>
            <CardDescription className="text-xs text-gray-400">{t("dashboard.clickSliceFilterDeck", "Clique em uma fatia para filtrar deck principal")}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center items-center">
            <div className="h-[250px] w-full relative">
              {!hasFlashcardsData ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6 bg-gray-50/30 rounded-2xl border border-dashed border-gray-100">
                  <AlertCircle className="w-8 h-8 mb-2 stroke-[1.2] text-gray-300 animate-pulse" />
                  <span className="text-xs font-bold text-gray-500">{t("dashboard.noFlashcardsRecordedYet", "Nenhum flashcard registrado ainda.")}</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={flashcardsDonut}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {flashcardsDonut.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.fill} 
                          className="cursor-pointer hover:opacity-80 transition-opacity focus:outline-none"
                          tabIndex={-1}
                          onClick={() => {
                            navigate(`/flashcards?difficulty=${entry.key}`);
                          }}
                        />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.05)' }} />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>


      </div>

      {/* DETAILED ATTEMPT SUMMARY MODAL */}
      <ContextModal
        isOpen={!!selectedAttempt}
        onClose={() => setSelectedAttempt(null)}
        title={t("history.detailsTitle", "Simulation Summary")}
        description={t("history.detailsDesc", "Historical record selected from the dashboard.")}
        size="md"
      >
        {selectedAttempt && (() => {
          const attempt = selectedAttempt as any;
          const isFull = selectedAttempt.mode === "full_exam";
          const isV2 = selectedAttempt.metadata?.type === "writing_linguistic_correction" && selectedAttempt.metadata?.writingVersion === 2;
          const isWriting = (selectedAttempt.competence === "Writing" || isV2) && !isFull;
          const levelInfo = !isFull && !isWriting ? getTefLevelByScore(selectedAttempt.competence, selectedAttempt.score) : null;
          
          let formattedDate = t("dashboard.dateUnavailable", "Data indisponível");
          try {
            formattedDate = new Date(selectedAttempt.date).toLocaleString(i18n.language, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            });
          } catch {}

          const sectionLabel = isFull ? t("history.sectionDisplay.fullSimulated", "Simulado Completo") : getSectionDisplay(selectedAttempt, t);
          const wordCount = selectedAttempt.words || selectedAttempt.sections?.writing?.words || selectedAttempt.metadata?.wordCount;
          const pctVal = selectedAttempt.percentage !== undefined 
            ? selectedAttempt.percentage 
            : parseScorePercentage(selectedAttempt.score);

          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <Badge className={
                  isFull ? "bg-purple-600 text-white border-none font-bold text-xs px-3 py-1" :
                  selectedAttempt.competence === "Reading" ? "bg-indigo-50 border-indigo-200 text-indigo-700 font-bold text-xs px-3 py-1" :
                  selectedAttempt.competence === "Listening" ? "bg-blue-50 border-blue-200 text-blue-700 font-bold text-xs px-3 py-1" :
                  "bg-emerald-50 border-emerald-200 text-emerald-700 font-bold text-xs px-3 py-1"
                }>
                  {isFull ? t("history.sectionDisplay.fullSimulated", "Simulado Completo") : translateCompetencyLabel(selectedAttempt.competence, t)}
                </Badge>
                <span className="text-xs text-gray-400 font-semibold">{formattedDate}</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 space-y-1">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.sectionTask", "Seção / Tarefa")}</span>
                  <span className="text-xs font-bold text-gray-800 leading-snug">{sectionLabel}</span>
                </div>
                
                <div className="bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 space-y-1">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.timeSpentCol", "Tempo Utilizado")}</span>
                  <span className="text-xs font-bold text-gray-800 leading-snug font-mono">{selectedAttempt.timeSpent || "N/A"}</span>
                </div>

                {isV2 ? (
                  <div className="bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 space-y-1 col-span-2">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.analysisType", "Tipo de Análise")}</span>
                    <span className="text-xs font-bold text-purple-700 leading-snug">
                      {t("history.pedagogicalCorrection", "Correção pedagógica")} ({selectedAttempt.metadata?.errorCount || 0} {selectedAttempt.metadata?.errorCount === 1 ? t("history.error", "erro") : t("history.errors", "erros")} · {selectedAttempt.metadata?.wordCount || 0} {t("history.words", "palavras")})
                    </span>
                  </div>
                ) : (
                  <>
                    {levelInfo && (
                      <div className="bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 space-y-1 col-span-2">
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.officialEquivalence", "Equivalência Oficial")}</span>
                        <span className="text-xs font-bold text-primary leading-snug font-mono">
                          {levelInfo.label} ({t("history.scoreRange", "Faixa")}: {levelInfo.range})
                        </span>
                      </div>
                    )}

                    {!isWriting && (
                      <div className="bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 space-y-1">
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.accuracy", "Aproveitamento")}</span>
                        <span className="text-xs font-bold text-gray-800 leading-snug font-mono">
                          {formatPercentage(pctVal)}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {isWriting && wordCount !== undefined && !isV2 && (
                  <div className="bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 space-y-1">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.wordsWritten", "Palavras Escritas")}</span>
                    <span className="text-xs font-bold text-gray-800 leading-snug font-mono">{wordCount} {t("history.words", "palavras")}</span>
                  </div>
                )}

                {!isV2 && ((selectedAttempt.score && typeof selectedAttempt.score === "string" && selectedAttempt.score.includes("/")) ||
                  ((attempt.correctAnswers ?? attempt.correct_answers) !== undefined && 
                   (attempt.correctAnswers ?? attempt.correct_answers) !== null && 
                   (attempt.maxScore ?? attempt.max_score))) && (
                  <div className="bg-gray-50/50 p-3.5 rounded-xl border border-gray-100 space-y-1">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">{t("history.correctAnswers", "Acertos / Total")}</span>
                    <span className="text-xs font-bold text-gray-800 leading-snug font-mono">
                      {typeof selectedAttempt.score === "string" && selectedAttempt.score.includes("/")
                        ? selectedAttempt.score
                        : `${attempt.correctAnswers ?? attempt.correct_answers} / ${attempt.maxScore ?? attempt.max_score}`}
                    </span>
                  </div>
                )}
              </div>

              {isV2 ? (
                <div className="pt-5 border-t border-gray-50 flex flex-col items-center justify-center p-5 bg-purple-50/40 rounded-2xl border border-purple-100/50">
                  <span className="text-[10px] font-bold text-purple-700/70 uppercase tracking-wider block mb-1">{t("history.result", "Resultado")}</span>
                  <div className="text-xl font-black text-purple-800 tracking-tight text-center">{t("history.aiPedagogicalCorrection", "Correção Pedagógica com IA")}</div>
                  <span className="text-[10px] text-gray-400 font-medium mt-1">{t("history.aiCorrectionDesc", "Foco na identificação de erros linguísticos e vocabulário")}</span>
                </div>
              ) : (
                <div className="pt-5 border-t border-gray-50 flex flex-col items-center justify-center p-5 bg-primary/5 rounded-2xl border border-primary/10">
                  <span className="text-[10px] font-bold text-primary/70 uppercase tracking-wider block mb-1">{t("history.finalScoreScale", "Nota Final (Escala TEF)")}</span>
                  <div className="text-2xl font-black text-primary font-mono tracking-tight text-center">{formatCompactScore(selectedAttempt.score)}</div>
                  {isWriting && (
                    <span className="text-[10px] text-gray-400 font-medium font-mono mt-1">{t("history.scoreFeedbackDesc", "Pontuação atribuída por feedback automatizado")}</span>
                  )}
                </div>
              )}

              <div className="pt-4 border-t border-gray-100 flex flex-wrap gap-2 justify-end">
                <Button 
                  variant="outline"
                  onClick={() => setSelectedAttempt(null)}
                  className="rounded-xl border-gray-100 font-bold text-xs"
                >
                  {t("history.close", "Fechar")}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => {
                    navigate(`/history?selectedId=${selectedAttempt.id}`);
                    setSelectedAttempt(null);
                  }}
                  className="rounded-xl border-gray-200 hover:bg-gray-50 text-gray-700 font-bold text-xs"
                >
                  {t("history.goToFullHistory", "Ir para o histórico completo")}
                </Button>

                <Button 
                  onClick={() => {
                    if (isFull) {
                      navigate(`/test/result?mode=full_exam&id=${selectedAttempt.id}`);
                    } else if (isV2) {
                      navigate("/test/result", {
                        state: {
                          fromHistory: true,
                          resultType: "writing_v2",
                          writingVersion: 2,
                          testType: selectedAttempt.metadata?.testMode || "writing_v2_single",
                          competency: "writing",
                          historyRecord: selectedAttempt,
                          writingTask: selectedAttempt.metadata?.writingTask,
                          writingCorrection: selectedAttempt.metadata?.writingCorrection,
                          writingTasks: selectedAttempt.metadata?.writingTasks || (selectedAttempt.metadata?.writingTask ? [selectedAttempt.metadata.writingTask] : [])
                        },
                      });
                    } else {
                      navigate(`/test/result?type=${selectedAttempt.competence.toLowerCase()}&words=${selectedAttempt.words || 0}&text=${encodeURIComponent(selectedAttempt.text || "")}`);
                    }
                    setSelectedAttempt(null);
                  }}
                  className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs shadow-sm"
                >
                  {t("dashboard.viewFullRevision", "Ver revisão completa")}
                </Button>
              </div>
            </div>
          );
        })()}
      </ContextModal>

      {/* EXPANDED CHART MODAL */}
      <ContextModal
        isOpen={isChartExpanded}
        onClose={() => setIsChartExpanded(false)}
        title={t("dashboard.scoreEvolutionFullHistory", "Evolução de Notas — Histórico Completo")}
        description={t("dashboard.scoreEvolutionDesc", "Acompanhamento detalhado das últimas 10 simulações de Compréhension Écrite (CE) e Compréhension Orale (CO).")}
        size="2xl"
      >
        <div className="space-y-6">
          <div className="h-[340px] w-full bg-gray-50/20 p-4 rounded-2xl border border-gray-100/60 overflow-visible">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={progressData} 
                margin={{ top: 56, right: 48, bottom: 10, left: 10 }}
                style={{ overflow: "visible" }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#4b5563', fontSize: 12}} dy={10} padding={{ left: 48, right: 48 }} />
                <YAxis domain={[0, yAxisMax]} allowDataOverflow={false} axisLine={false} tickLine={false} tick={{fill: '#4b5563', fontSize: 12}} />
                <Tooltip 
                  contentStyle={{ borderRadius: '14px', border: 'none', boxShadow: '0 10px 25px -5px rgb(0 0 0 / 0.1)' }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', paddingTop: '15px' }} />
                <Line 
                  type="monotone" 
                  dataKey="reading" 
                  stroke="#8b5cf6" 
                  strokeWidth={3} 
                  dot={<CustomClickableDot dataKey="reading" />}
                  activeDot={<CustomClickableDot dataKey="reading" />}
                  name={t("dashboard.ceScale", "Compréhension Écrite - CE (Escala 300)")} 
                  connectNulls 
                />
                <Line 
                  type="monotone" 
                  dataKey="listening" 
                  stroke="#3b82f6" 
                  strokeWidth={3} 
                  dot={<CustomClickableDot dataKey="listening" />}
                  activeDot={<CustomClickableDot dataKey="listening" />}
                  name={t("dashboard.coScale", "Compréhension Orale - CO (Escala 360)")} 
                  connectNulls 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-2.5">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t("dashboard.lastRecordedActivities", "Últimas Atividades Registradas")}</h4>
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 font-bold border-b border-gray-100">
                    <th className="py-3 px-4">{t("dashboard.tableDate", "Data")}</th>
                    <th className="py-3 px-4">{t("dashboard.tableCompetence", "Competência")}</th>
                    <th className="py-3 px-4">{t("dashboard.tableScore", "Nota / Total")}</th>
                    <th className="py-3 px-4 text-right">{t("dashboard.tableAction", "Ação")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {progressData.slice().reverse().map((p: any) => {
                    let formattedDate = "N/A";
                    try {
                      formattedDate = new Date(p.date).toLocaleDateString(t("common.localeCode", "pt-BR"), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
                    } catch {}
                    return (
                      <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4 font-medium text-gray-600">{formattedDate}</td>
                        <td className="py-3 px-4">
                          <Badge className={`text-[10px] font-semibold border-none px-2 py-0.5 ${
                            p.competency === "Simulado Completo" ? "bg-purple-100 text-purple-700" :
                            p.competency === "Reading" ? "bg-indigo-100 text-indigo-700" : "bg-blue-100 text-blue-700"
                          }`}>
                            {getCompetencyLabel(p.competency)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 font-bold text-gray-800 font-mono">{p.score}</td>
                        <td className="py-3 px-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleScorePointClick(p.id)}
                            className="text-primary hover:text-primary/90 hover:bg-primary/5 font-bold h-7 px-2.5 rounded-lg"
                          >
                            Detalhes
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex gap-2 justify-end">
            <Button 
              variant="outline"
              onClick={() => setIsChartExpanded(false)}
              className="rounded-xl border-gray-100 font-bold text-xs"
            >
              Fechar
            </Button>
            <Button 
              onClick={() => {
                navigate("/history");
                setIsChartExpanded(false);
              }}
              className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs shadow-sm"
            >
              Ir para o histórico completo
            </Button>
          </div>
        </div>
      </ContextModal>
      {/* EDIT PERSONAL GOALS MODAL */}
      <ContextModal
        isOpen={isGoalModalOpen}
        onClose={() => setIsGoalModalOpen(false)}
        title="Editar Metas Pessoais"
        description="Ajuste os níveis desejados para cada competência do exame."
        size="md"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 block">Meta CE (NCLC)</label>
              <select
                value={editingGoals.targetCeNclc}
                onChange={e => setEditingGoals(prev => ({ ...prev, targetCeNclc: Number(e.target.value) }))}
                className="w-full bg-gray-50 text-gray-800 font-bold text-sm rounded-xl px-3 py-2.5 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {[4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>NCLC {n}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 block">Meta CO (NCLC)</label>
              <select
                value={editingGoals.targetCoNclc}
                onChange={e => setEditingGoals(prev => ({ ...prev, targetCoNclc: Number(e.target.value) }))}
                className="w-full bg-gray-50 text-gray-800 font-bold text-sm rounded-xl px-3 py-2.5 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {[4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>NCLC {n}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 block">Horas de estudo / semana</label>
              <input 
                type="number"
                min={1}
                max={40}
                value={editingGoals.weeklyStudyHours}
                onChange={e => setEditingGoals(prev => ({ ...prev, weeklyStudyHours: Number(e.target.value) }))}
                className="w-full bg-gray-50 text-gray-800 font-bold text-sm rounded-xl px-3 py-2 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-gray-700 block">Data da prova</label>
              <input 
                type="date"
                value={editingGoals.examDate}
                onChange={e => setEditingGoals(prev => ({ ...prev, examDate: e.target.value }))}
                className="w-full bg-gray-50 text-gray-800 font-bold text-sm rounded-xl px-3 py-2 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex gap-2 justify-end">
            <Button 
              variant="outline"
              disabled={savingGoals}
              onClick={() => setIsGoalModalOpen(false)}
              className="rounded-xl border-gray-200 font-bold text-xs"
            >
              Cancelar
            </Button>
            <Button 
              disabled={savingGoals}
              onClick={handleSaveGoals}
              className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs shadow-sm"
            >
              {savingGoals && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
              Salvar metas
            </Button>
          </div>
        </div>
      </ContextModal>

      {/* Floating Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 right-6 z-50 bg-gray-900 text-white font-bold text-xs py-3.5 px-6 rounded-2xl shadow-xl flex items-center gap-3 animate-in slide-in-from-top duration-300">
          <div className="w-2 h-2 rounded-full bg-red-400 animate-ping shrink-0" />
          <span className="flex-1">{toastMessage}</span>
          <button 
            onClick={() => navigate("/history")} 
            className="underline hover:text-gray-200 transition-colors ml-1 font-extrabold cursor-pointer shrink-0"
          >
            {t("dashboard.openHistory", "Abrir Histórico")}
          </button>
        </div>
      )}

      {/* BUILD CHECK MARKER */}
      <div className="w-full text-center py-4 mt-8">
        <span className="text-[9px] text-gray-300 font-mono tracking-widest opacity-50 hover:opacity-100 transition-opacity">BUILD CHECK: i18n-fr-cleanup-active</span>
      </div>
    </div>
  );
}
